// TaskModal.jsx — versão desktop mantida + otimizações mobile-first (arquivo completo)
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  TextInput,
  Select,
  Avatar,
  Badge,
  Spinner,
} from "flowbite-react";
import {
  HiOutlinePencil,
  HiTrash as HiOutlineTrash,
  HiPhone,
  HiVideoCamera,
  HiMail,
  HiUserGroup,
  HiRefresh,
  HiChatAlt2,
  HiAnnotation,
  HiCurrencyDollar,
  HiOutlineX,
  HiPaperClip,
} from "react-icons/hi";
import { GrDocumentPpt } from "react-icons/gr";
import axios from "../../config/axiosInstance";
import {
  ACTIVITIES,
  BUDGETS,
  CRM,
  INTEGRATIONS,
  LEADS,
  STAGE_FORMS,
  USERS,
} from "../../config/apiConfig";
import StageTimeline from "../tables/StageTimeline";
import AutoCompleteInput from "../inputs/Autocomplete";
import TagPicker from "./TagPicker";
import ReceitaTooltip from "./ReceitaTooltip";
import LeadContactsPanel from "../crm/LeadContactsPanel";
import AttachmentPicker from "../inputs/AttachmentPicker";
import TaskTimePicker from "../inputs/TaskTimePicker";
import TaskEditModal from "./TaskEditModal";
import TiptapEditor from "../inputs/TipTapEditor";
import StageCustomForm from "../crm/StageCustomForm";
import LeadCreateForm from "../forms/LeadCreateForm";
import { useQuery, useQueryClient } from "react-query";
import { LiaCommentDotsSolid } from "react-icons/lia";

import {
  fmt,
  formatAnswer,
  hasHtmlTags,
  escapeHtml,
  toRichHtml,
  stripVisualEmpty,
  fmtBR,
  buildLocalISO,
  prettySize,
  fileIconFor,
  invalidateStats,
  dateToHHMM,
  labelIcon,
  labelMap,
  formatDocument,
} from "../../utils/utils";
import TaskItem from "../crm/TaskItem";
import TaskListCompact from "../crm/TaskListCompact";
import {
  MdComment,
  MdKeyboardArrowUp,
  MdOutlineKeyboardArrowDown,
  MdOutlineModeEdit,
} from "react-icons/md";
import { FaRegTrashCan, FaWpforms } from "react-icons/fa6";
import { IoMdPricetags } from "react-icons/io";
import { GoTasklist } from "react-icons/go";
import { BsInfo, BsInfoCircle } from "react-icons/bs";
import { Tooltip } from "react-tooltip";
import { RxActivityLog } from "react-icons/rx";
import moment from "moment";
import { useApplication } from "../../contexts/ApplicationContext";

const onlyDigits = (value = "") => String(value || "").replace(/\D/g, "");

const formatPhoneNumber = (value = "") => {
  const digits = onlyDigits(value);
  if (!digits) return null;
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

const normalizeLeadPayload = (lead) => {
  if (!lead) return null;
  if (typeof lead.toJSON === "function") return lead.toJSON();
  if (lead.dataValues && typeof lead.dataValues === "object") {
    return { ...lead.dataValues };
  }
  return { ...lead };
};

const ACTION_LABELS = {
  CARD_CREATED: "Card criado",
  CARD_UPDATED: "Card atualizado",
  CARD_COMMENT_ADDED: "Comentário no card",
  TASK_CREATED: "Tarefa criada",
  TASK_UPDATED: "Tarefa atualizada",
  TASK_DONE: "Tarefa concluída",
  TASK_DELETED: "Tarefa excluída",
  CARD_CUSTOM_FIELDS_SET: "Propriedades atualizadas",
  CARD_ATTACHMENT_ADDED: "Anexo incluído no card",
  CARD_ATTACHMENT_DELETED: "Anexo excluído no card",
  CARD_CREATED_FROM_PUBLIC_FORM: "Card criado através do formulário",
  CARD_MOVED_STAGE: "Mudança de Etapa"
};

const ACTION_COLORS = {
  CARD_CREATED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CARD_UPDATED: "bg-amber-50 text-amber-700 border-amber-200",
  CARD_COMMENT_ADDED: "bg-sky-50 text-sky-700 border-sky-200",
  TASK_CREATED: "bg-violet-50 text-violet-700 border-violet-200",
  TASK_UPDATED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  TASK_DONE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  TASK_DELETED: "bg-red-50 text-red-700 border-red-200",
  CARD_CUSTOM_FIELDS_SET: "bg-indigo-50 text-indigo-700 border-indigo-200",
  CARD_ATTACHMENT_ADDED: "bg-yellow-50 text-yellow-700 border-yellow-200",
  CARD_ATTACHMENT_DELETED: "bg-red-50 text-red-700 border-red-200",
};

const getActionLabel = (action) => ACTION_LABELS[action] || action;
const getActionColor = (action) =>
  ACTION_COLORS[action] || "bg-gray-50 text-gray-700 border-gray-200";

function CustomFieldsSection({
  fields = [],
  values = {},
  onChange,
  onBlurField,
}) {
  const [openMultiKey, setOpenMultiKey] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenMultiKey(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!fields.length) return null;

  const getValue = (field) => {
    const key = field.key || field.fieldKey || field.name;
    return values[key] ?? "";
  };

  const handleChange = (field, rawValue) => {
    if (!onChange) return;
    const key = field.key || field.fieldKey || field.name;
    if (!key) return;
    onChange(field, rawValue);
  };

  const handleBlur = (field, rawValue) => {
    if (!onBlurField) return;
    onBlurField(field, rawValue);
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-2">
        Propriedades do card
      </h3>
      <div className="rounded-2xl border border-slate-200 bg-gray-100 p-3 md:p-4 space-y-3">
        <div className="flex flex-col gap-3" ref={containerRef}>
          {fields.map((field) => {
            const key = field.id || field.key || field.fieldKey || field.name;

            // normaliza tipo vindo do backend
            const rawType = (field.type || "text").toLowerCase();
            const type =
              rawType === "single_select"
                ? "select"
                : rawType === "multi_select"
                ? "multiselect"
                : rawType;

            const required = !!field.required;
            const options = Array.isArray(field.options) ? field.options : [];
            const raw = getValue(field);

            // normaliza multi-select
            const multiSelected =
              type === "multiselect"
                ? Array.isArray(raw)
                  ? raw
                  : raw
                  ? String(raw)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : []
                : [];

            const tooltipId = `cf-tooltip-${key}`;
            const hasDescription = !!field.description;

            const labelNode = (
              <>
                <div className="flex items-center justify-between gap-1 px-1 mb-1">
                  <label className="block text-[13px] font-medium text-slate-600">
                    {field.label || key}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>

                  {hasDescription && (
                    <>
                      <button
                        type="button"
                        className="p-1 rounded-full hover:bg-slate-100 text-gray-500 transition-colors"
                        data-tooltip-id={tooltipId}
                        data-tooltip-content={field.description}
                      >
                        <BsInfoCircle className="text-gray-500 text-sm" />
                      </button>

                      <Tooltip
                        id={tooltipId}
                        place="top"
                        className="!text-xs !max-w-xs"
                      />
                    </>
                  )}
                </div>
              </>
            );

            // TEXTAREA
            if (type === "textarea") {
              return (
                <div key={key} className="sm:col-span-2">
                  {labelNode}
                  <textarea
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:border-slate-400 focus:ring-slate-300"
                    value={raw || ""}
                    onChange={(e) => handleChange(field, e.target.value)}
                    onBlur={(e) => handleBlur(field, e.target.value)} // ⬅️
                    placeholder={field.placeholder || ""}
                  />
                </div>
              );
            }

            // SELECT
            if (type === "select") {
              return (
                <div key={key}>
                  {labelNode}
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white text-[13px] text-slate-800 focus:border-slate-400 focus:ring-slate-300"
                    value={raw || ""}
                    onChange={(e) =>
                      handleChange(field, e.target.value || null)
                    }
                    onBlur={(e) => handleBlur(field, e.target.value || null)} // ⬅️
                  >
                    <option value="">Selecione…</option>
                    {options.map((opt) => {
                      const val =
                        typeof opt === "string" ? opt : opt.value ?? opt.label;
                      const lab =
                        typeof opt === "string" ? opt : opt.label ?? opt.value;
                      return (
                        <option key={val} value={val}>
                          {lab}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            }

            // MULTISELECT

            if (type === "multiselect") {
              const selectedValues = Array.isArray(multiSelected)
                ? multiSelected
                : [];

              const isOpen = openMultiKey === key;

              const toggleOption = (val) => {
                let next = selectedValues.includes(val)
                  ? selectedValues.filter((v) => v !== val)
                  : [...selectedValues, val];

                handleChange(field, next); // atualiza estado local
                handleBlur(field, next); // salva automaticamente
              };

              const renderSelectedLabel = () => {
                if (!selectedValues.length) {
                  return (
                    <span className="text-[12px] text-slate-400">
                      {field.placeholder || "Selecione..."}
                    </span>
                  );
                }

                if (selectedValues.length === 1) {
                  return (
                    <span className="text-[12px] text-slate-700">
                      {(() => {
                        const opt = options.find((o) => {
                          const val =
                            typeof o === "string" ? o : o.value ?? o.label;
                          return val === selectedValues[0];
                        });
                        if (!opt) return selectedValues[0];
                        return typeof opt === "string"
                          ? opt
                          : opt.label ?? opt.value;
                      })()}
                    </span>
                  );
                }

                // 2+ selecionadas: mostra contagem
                return (
                  <span className="text-[12px] text-slate-700">
                    {selectedValues.length} opções selecionadas
                  </span>
                );
              };

              return (
                <div key={key} className="space-y-2 relative">
                  {labelNode}

                  {/* Trigger do dropdown */}
                  <button
                    type="button"
                    className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[12px] text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    onClick={() => setOpenMultiKey(isOpen ? null : key)}
                  >
                    <div className="flex flex-wrap gap-1">
                      {renderSelectedLabel()}
                    </div>

                    <span className="ml-2 text-[13px] text-slate-400">
                      {isOpen ? (
                        <MdKeyboardArrowUp />
                      ) : (
                        <MdOutlineKeyboardArrowDown />
                      )}
                    </span>
                  </button>

                  {/* Lista de opções */}
                  {isOpen && (
                    <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-52 overflow-auto">
                      {options.map((opt) => {
                        const val =
                          typeof opt === "string"
                            ? opt
                            : opt.value ?? opt.label;
                        const lab =
                          typeof opt === "string"
                            ? opt
                            : opt.label ?? opt.value;
                        const checked = selectedValues.includes(val);

                        return (
                          <button
                            key={val}
                            type="button"
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-left hover:bg-slate-50 ${
                              checked ? "bg-gray-200" : ""
                            }`}
                            onClick={() => toggleOption(val)}
                          >
                            <input
                              type="checkbox"
                              readOnly
                              checked={checked}
                              className="h-3 w-3 rounded border-slate-300 text-slate-700"
                            />
                            <span className="text-slate-700">{lab}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {field.description && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      {field.description}
                    </p>
                  )}
                </div>
              );
            }

            // BOOLEAN
            if (type === "boolean") {
              return (
                <div
                  key={key}
                  className="flex flex-col sm:flex-row sm:items-center gap-1.5 my-3"
                >
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                      checked={!!raw}
                      onChange={(e) => {
                        const val = e.target.checked;
                        handleChange(field, val);
                        handleBlur(field, val); // ⬅️ salva na hora
                      }}
                    />
                    <span className="text-sm text-slate-700">
                      {field.label || key}
                    </span>
                  </label>
                </div>
              );
            }

            // NUMBER / CURRENCY
            // NUMBER
            if (type === "number") {
              return (
                <div key={key}>
                  {labelNode}
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:border-slate-400 focus:ring-slate-300"
                    value={raw ?? ""}
                    onChange={(e) => handleChange(field, e.target.value)}
                    onBlur={(e) => handleBlur(field, e.target.value)}
                    placeholder={field.placeholder || ""}
                  />
                </div>
              );
            }

            // CURRENCY (R$ 1.234,56)
            if (type === "currency") {
              // normaliza o valor que vem do map (pode ser number ou string)
              let displayValue = "";

              if (raw !== null && raw !== undefined && raw !== "") {
                if (typeof raw === "number") {
                  // veio direto do valueNumber (backend)
                  displayValue = raw.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                } else if (typeof raw === "string") {
                  if (raw.includes("R$")) {
                    // já está formatado, só usa
                    displayValue = raw;
                  } else {
                    // string sem máscara (ex: "1234.56" ou "123456")
                    const digitsOnly = raw.replace(/\D/g, "");
                    if (digitsOnly) {
                      const number = Number(digitsOnly) / 100;
                      if (Number.isFinite(number)) {
                        displayValue = number.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                      } else {
                        displayValue = raw;
                      }
                    } else {
                      displayValue = "";
                    }
                  }
                }
              }

              const handleCurrencyChange = (e) => {
                const input = e.target.value;

                // permite apagar tudo
                const digitsOnly = input.replace(/\D/g, "");
                if (!digitsOnly) {
                  handleChange(field, "");
                  return;
                }

                const number = Number(digitsOnly) / 100;

                const formatted = number.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });

                // salva já mascarado no estado do front
                handleChange(field, formatted);
              };

              const handleCurrencyBlur = (e) => {
                // backend vai desmascarar em mapCustomFieldValueToColumns
                handleBlur(field, e.target.value || "");
              };

              return (
                <div key={key}>
                  {labelNode}
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:border-slate-400 focus:ring-slate-300"
                    value={displayValue}
                    onChange={handleCurrencyChange}
                    onBlur={handleCurrencyBlur}
                    placeholder={field.placeholder || "R$ 0,00"}
                  />
                </div>
              );
            }

            // DATE / DATETIME
            if (type === "date" || type === "datetime") {
              return (
                <div key={key}>
                  {labelNode}
                  <input
                    type={type === "date" ? "date" : "datetime"}
                    className="w-full rounded-lg border border-slate-200 bg-white text-[13px] text-slate-800 placeholder-slate-400 focus:border-slate-400 focus:ring-slate-300"
                    value={raw || ""}
                    onChange={(e) => handleChange(field, e.target.value)}
                    onBlur={(e) => handleBlur(field, e.target.value)} // ⬅️
                    placeholder={field.placeholder || ""}
                  />
                </div>
              );
            }

            // DEFAULT: TEXT
            return (
              <div key={key}>
                {labelNode}
                <input
                  type="text"
                  className="w-full rounded-lg border border-slate-200 bg-white text-[13px] text-slate-800 placeholder-slate-400 focus:border-slate-400 focus:ring-slate-300"
                  value={raw || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  onBlur={(e) => handleBlur(field, e.target.value)} // ⬅️
                  placeholder={field.placeholder || ""}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Converte o formato novo (fields + customFieldValues) em map { key: valor }
const buildCustomFieldValuesMap = (task) => {
  if (!task) return {};

  const fields = task.fields || task.customFields || [];
  const values = task.customFieldValues || [];

  // Backward compat: se o backend ainda mandar um objeto pronto, usa ele
  if (
    task.customFields &&
    !Array.isArray(task.customFields) &&
    !values.length
  ) {
    return task.customFields;
  }

  const map = {};

  values.forEach((cv) => {
    const field =
      cv.field ||
      (fields || []).find((f) => String(f.id) === String(cv.customFieldId));

    if (!field) return;

    const key = field.key || field.fieldKey || field.name || String(field.id);

    const rawType = (field.type || "text").toLowerCase();
    let raw = null;

    switch (rawType) {
      case "number":
      case "currency": {
        raw = cv?.valueNumber ?? "";
        break;
      }

      case "boolean": {
        // salvou como 1/0 em valueNumber
        const n = cv?.valueText;
        if (n === null || n === undefined) {
          raw = false;
        } else if (n == "true") {
          raw = true;
        } else {
          raw = false;
        }
        break;
      }

      case "date": {
        // valueDate normalmente vem como ISO
        raw = cv?.valueDate
          ? String(cv.valueDate).replace("T00:00:00.000Z", "")
          : "";
        break;
      }

      case "datetime": {
        // deixa em ISO/string para o input usar/formatar
        raw = cv?.valueDate || "";
        break;
      }

      case "multi_select": {
        // array de valores
        raw = Array.isArray(cv?.valueJson) ? cv.valueJson : [];
        break;
      }

      // text, textarea, single_select, qualquer outro
      default: {
        raw = cv?.valueText ?? cv?.valueJson ?? "";
        break;
      }
    }

    if (raw !== undefined) {
      map[key] = raw;
    }
  });

  return map;
};

export default function TaskModal({
  task,
  onClose,
  updateTask,
  history,
  setHistory,
  setSelectedTask,
  stages,
  onMoveStage,
  onRefetchCard,
  onCardDeleted,
}) {
  const { userPermission } = useApplication();
  const leadId = task?.lead?.id || null;
  const qc = useQueryClient();
  const [title, setTitle] = useState(task?.title || "Visualizar card");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isUploadingTask, setIsUploadingTask] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const [actionFiles, setActionFiles] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachmentsUploadPct, setAttachmentsUploadPct] = useState(0);
  const [cardAttachmentLoading, setCardAttachmentLoading] = useState({});
  const [removingAttachmentIds, setRemovingAttachmentIds] = useState({});
  const [editFiles, setEditFiles] = useState([]);
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);
  const [savingCustomFields, setSavingCustomFields] = useState(false);

  const [confirmCompleteTask, setConfirmCompleteTask] = useState(null); // null | task

  const openConfirmComplete = (t) => setConfirmCompleteTask(t);
  const closeConfirmComplete = () => setConfirmCompleteTask(null);

  const confirmCompleteNow = async () => {
    const t = confirmCompleteTask;
    if (!t) return;
    await handleToggleTaskStatus(t, true);
    closeConfirmComplete();
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [taskBeingEdited, setTaskBeingEdited] = useState(null);

  // comentários com HTML (Tiptap)
  const [commentHtml, setCommentHtml] = useState("");
  const [commentRich, setCommentRich] = useState(false); // mobile: alternar textarea/Tiptap

  // responsável do CARD
  const [isEditingResponsible, setIsEditingResponsible] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(
    task?.responsible?.id || ""
  );

  // cliente do CARD
  const [showLeadEditModal, setShowLeadEditModal] = useState(false);

  // 🔧 Dados do cliente atual
  const customer = task?.lead || {};
  const customerEmail = customer?.email || customer?.emails?.[0] || "";
  const customerPhone = customer?.phoneNumber || "";
  const customerDocument = customer?.document || "";
  const customerResponsible = customer?.responsibleName || customer?.name || "";
  const linkedBudget = task?.budget || null;
  const linkedBudgetValue =
    Number(linkedBudget?.totalValue ?? linkedBudget?.subtotal ?? 0) || 0;
  const hasLinkedBudget = Boolean(linkedBudget);

  const [currentUser, setCurrentUser] = useState(null);

  // edição/exclusão de comentários
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentHtml, setEditCommentHtml] = useState("");

  // criação de PRÓXIMA AÇÃO
  const [actionType, setActionType] = useState(task?.nextActionType || "");
  const [actionNoteHtml, setActionNoteHtml] = useState(
    task?.nextActionNote || ""
  );
  const [actionDueDate, setActionDueDate] = useState(
    task?.nextActionDueDate ? String(task.nextActionDueDate).slice(0, 10) : ""
  );
  const [actionDueTime, setActionDueTime] = useState(
    task?.nextActionDueDate ? dateToHHMM(new Date(task.nextActionDueDate)) : ""
  );
  const [actionPhone, setActionPhone] = useState("");
  const [actionResponsibleIds, setActionResponsibleIds] = useState(
    extractResponsibleIdsFromTask(task)
  );
  const [calendarAvailability, setCalendarAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [showRichAction, setShowRichAction] = useState(false); // mobile: textarea -> editor rico

  const { data: integrationTokens = [] } = useQuery(
    "integration.tokens",
    async () => {
      const response = await axios.get(INTEGRATIONS.LIST_TOKENS);
      return response?.data?.data || [];
    },
    {
      staleTime: 1000 * 60 * 5,
    }
  );
  const hasGoogleCalendar = integrationTokens.some(
    (token) =>
      String(token?.tokenType || "").toLowerCase() === "google_calendar"
  );

  useEffect(() => {
    let cancelled = false;
    if (!hasGoogleCalendar || !actionDueDate) {
      setCalendarAvailability([]);
      setAvailabilityError("");
      setAvailabilityLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const [year, month, day] = (actionDueDate || "")
      .split("-")
      .map((value) => Number(value));
    const due =
      year && month && day ? new Date(year, month - 1, day, 0, 0, 0, 0) : null;
    if (!due || Number.isNaN(due.getTime())) {
      setCalendarAvailability([]);
      return () => {
        cancelled = true;
      };
    }
    const start = new Date(due);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);

    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const params = {
          timeMin: start.toISOString(),
          timeMax: end.toISOString(),
          maxResults: 50,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const response = await axios.get(INTEGRATIONS.GOOGLE_CALENDAR_EVENTS, {
          params,
        });
        if (!cancelled) {
          setCalendarAvailability(response?.data?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailabilityError(
            error?.response?.data?.message ||
              "Não foi possível buscar compromissos."
          );
          setCalendarAvailability([]);
        }
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    };

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [hasGoogleCalendar, actionDueDate]);

  const formatEventTimeRange = (event) => {
    const start = event?.start?.dateTime || event?.start?.date;
    const end = event?.end?.dateTime || event?.end?.date;
    if (!start) return "Sem horário";
    if (!event?.start?.dateTime) return "Dia inteiro";
    const startLabel = moment(start).format("HH:mm");
    const endLabel = end ? moment(end).format("HH:mm") : "";
    return endLabel ? `${startLabel} — ${endLabel}` : startLabel;
  };

  const conflictingEvents = useMemo(() => {
    if (!calendarAvailability?.length || !actionDueDate || !actionDueTime) {
      return [];
    }
    const dueISO = buildLocalISO(actionDueDate, actionDueTime);
    if (!dueISO) return [];
    const dueMoment = moment(dueISO);
    if (!dueMoment.isValid()) return [];

    return calendarAvailability.filter((event) => {
      const startRaw = event?.start?.dateTime || event?.start?.date;
      if (!startRaw) return false;
      const startMoment = moment(startRaw);
      if (!startMoment.isValid()) return false;

      if (!event?.start?.dateTime) {
        return dueMoment.isSame(startMoment, "day");
      }

      const endRaw = event?.end?.dateTime || event?.end?.date;
      const endMoment =
        endRaw && moment(endRaw).isValid()
          ? moment(endRaw)
          : startMoment.clone().add(1, "hour");
      return dueMoment.isBetween(startMoment, endMoment, null, "[)");
    });
  }, [calendarAvailability, actionDueDate, actionDueTime]);

  // edição inline de tarefas existentes
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editType, setEditType] = useState("");
  const [editNoteHtml, setEditNoteHtml] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editDueTime, setEditDueTime] = useState("");
  const [editResponsibleIds, setEditResponsibleIds] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // TAGS do card
  const [currentTag, setCurrentTag] = useState(task?.tag || null);

  const [showTagPicker, setShowTagPicker] = useState(false);
  const companyIdForTags = task?.lead?.id || task?.companyId || null;
  const selectedStage = (stages || []).find(
    (st) => String(st.id) === String(task?.stageId)
  );
  const funnelIdForTags =
    selectedStage?.funnelId || task?.funnelId || task?.stage?.funnelId || null;
  const canManageTags = Boolean(companyIdForTags && funnelIdForTags);
  const openTagPicker = () => {
    if (!canManageTags) return;
    setShowTagPicker(true);
  };
  const [deleting, setDeleting] = useState(false);

  // Mobile State
  const [mobileTab, setMobileTab] = useState("tarefas"); // 'detalhes'|'tarefas'|'comentarios'|'acao'

  // 2) TODAS as respostas de form deste card (histórico completo)
  const {
    data: activities,
    isLoading: loadingActivities,
    refetch: refetchActivities,
  } = useQuery(
    ["activities", task?.id],
    async () => {
      const res = await axios.get(ACTIVITIES.LIST_BY_CARD(task?.id));
      return Array.isArray(res?.data?.data) ? res.data.data : [];
    },
    { enabled: !!task?.id }
  );

  const handleDeleteCard = async () => {
    if (!task?.id) return;
    const ok = window.confirm(
      "Excluir este card? Todas as tarefas e histórico ligados a ele serão removidos."
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await axios.delete(`${CRM.DELETE_CARD}/${task.id}`);

      if (typeof onCardDeleted === "function") {
        onCardDeleted(task.id);
      }

      onClose?.();
    } catch (e) {
      console.error("Erro ao excluir card:", e);
      alert("Não foi possível excluir o card.");
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = async () => {
    try {
      await onRefetchCard?.(task?.id);
    } finally {
      onClose?.();
    }
  };

  const handleAssignTag = async (tagOrNull) => {
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, {
        tagId: tagOrNull ? tagOrNull.id : null,
      });
      const updatedAt = new Date().toISOString();
      setCurrentTag(tagOrNull || null);
      updateTask({ ...task, tag: tagOrNull || null, updatedAt });
      setSelectedTask?.((prev) => ({
        ...prev,
        tag: tagOrNull || null,
        updatedAt,
      }));
    } catch (e) {
      console.error("Erro ao atribuir tag ao card:", e);
      alert("Não foi possível atualizar a etiqueta do card.");
    }
  };

  const ACTION_PRESETS = [
    { key: "ligacao", label: "Ligação", icon: HiPhone },
    { key: "reuniao", label: "Reunião", icon: HiVideoCamera },
    { key: "email", label: "E-mail", icon: HiMail },
    { key: "visita", label: "Visita", icon: HiUserGroup },
    { key: "followup", label: "Follow-up", icon: HiRefresh },
    { key: "whatsapp", label: "WhatsApp", icon: HiChatAlt2 },
    { key: "proposta", label: "Proposta", icon: GrDocumentPpt },
  ];
  const defaultNoteFor = (k) => {
    switch (k) {
      case "ligacao":
        return "<p>Ligar para o cliente</p>";
      case "reuniao":
        return "<p>Agendar reunião com o cliente</p>";
      case "email":
        return "<p>Enviar e-mail para o cliente</p>";
      case "visita":
        return "<p>Agendar visita com o cliente</p>";
      case "followup":
        return "<p>Fazer follow-up</p>";
      case "whatsapp":
        return "<p>Enviar mensagem no WhatsApp</p>";
      case "proposta":
        return "<p>Enviar proposta para o cliente.</p>";
      default:
        return "<p>Definir próxima ação</p>";
    }
  };

  // ===== Fetch base =====
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        setAvailableUsers(res.data?.data?.data || []);
      } catch (err) {
        console.error("Erro ao buscar usuários:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    const fetchMe = async () => {
      try {
        const r = await axios.get(USERS.GET_USER_CURRENT);
        setCurrentUser(r?.data?.data || r?.data?.user || null);
      } catch (e) {
        console.warn("Não foi possível obter o usuário logado (ME).", e);
      }
    };
    fetchUsers();
    fetchMe();
  }, []);

  const userOptions = useMemo(
    () =>
      (availableUsers || []).map((u) => ({
        value: u.id,
        label: `${u.name || "(sem nome)"}${u.email ? ` — ${u.email}` : ""}`,
      })),
    [availableUsers]
  );

  const [leadFormInitialData, setLeadFormInitialData] = useState(null);
  const [leadFormLoading, setLeadFormLoading] = useState(false);

  useEffect(() => {
    let canceled = false;
    if (!showLeadEditModal || !task?.lead?.id) {
      setLeadFormInitialData(null);
      setLeadFormLoading(false);
      return () => {
        canceled = true;
      };
    }
    const loadLead = async () => {
      setLeadFormLoading(true);
      try {
        const { data } = await axios.get(LEADS.GET(task.lead.id));
        if (canceled) return;
        const payload = data?.data || data?.lead || data || {};
        const normalizedLead = normalizeLeadPayload(payload) || {};
        setLeadFormInitialData({
          ...normalizedLead,
          tagId: normalizedLead.tagId || normalizedLead?.tag?.id || "",
        });
      } catch (error) {
        console.error("TaskModal: erro ao carregar lead para edição", error);
        const fallbackLead = normalizeLeadPayload(task?.lead) || {};
        setLeadFormInitialData({
          ...fallbackLead,
          tagId: fallbackLead.tagId || fallbackLead?.tag?.id || "",
        });
      } finally {
        if (!canceled) setLeadFormLoading(false);
      }
    };
    loadLead();
    return () => {
      canceled = true;
    };
  }, [showLeadEditModal, task?.lead?.id, task?.lead]);

  const getUserNameById = (id) => {
    if (!id) return "";
    const u = (availableUsers || []).find((x) => String(x.id) === String(id));
    return u?.name || "";
  };

  function extractResponsibleIdsFromTask(t) {
    if (!t) return [];
    const fromList = Array.isArray(t.responsibles)
      ? t.responsibles.map((r) => r?.id).filter(Boolean)
      : [];
    if (fromList.length) return fromList;
    const fallback = t?.responsibleId || t?.responsible?.id;
    return fallback ? [fallback] : [];
  }

  const parseDueTimestamp = (task) => {
    if (!task) return null;
    const value = task?.dueDate ? Date.parse(task.dueDate) : null;
    return Number.isNaN(value) ? null : value;
  };

  const parseTimestampValue = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const sortTasksByDueDate = (list = []) => {
    return [...list].sort((a, b) => {
      const aValue = parseDueTimestamp(a);
      const bValue = parseDueTimestamp(b);
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      return aValue - bValue;
    });
  };

  const reloadTasks = async () => {
    try {
      setLoadingTasks(true);
      const res = await axios.get(`${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setTasks(sortTasksByDueDate(list));
    } catch (err) {
      console.error("Erro ao buscar tarefas:", err);
    } finally {
      setLoadingTasks(false);
    }
  };
  useEffect(() => {
    if (task?.id) reloadTasks();
  }, [task?.id]);

  useEffect(() => {
    setActionResponsibleIds(extractResponsibleIdsFromTask(task));
  }, [task?.id, task?.responsibleId]);

  // 🔧 Re-sincroniza os campos do cliente quando trocar de card
  useEffect(() => {
    setCurrentTag(task?.tag);
    setCustomFieldValues(buildCustomFieldValuesMap(task));
    setCustomFieldsDirty(false);
  }, [task?.id, task?.stageId, task?.customFieldValues?.length, task?.tag]);

  // ===== Saves do CARD =====

  const handleSaveResponsible = async (opt) => {
    try {
      const newId = opt?.value || "";
      setSelectedResponsible(newId);
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, {
        responsibleId: newId || null,
      });
      const userObj = availableUsers.find((u) => u.id == newId) || null;
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, responsible: userObj, updatedAt });
      setSelectedTask?.((prev) => ({
        ...prev,
        responsible: userObj,
        updatedAt,
      }));
      setIsEditingResponsible(false);
    } catch (err) {
      console.error("Erro ao salvar responsável:", err);
    }
  };

  const handleLeadUpdate = async (payload) => {
    if (!task?.lead?.id) {
      throw new Error("Lead inválido para atualização");
    }
    const { data } = await axios.put(LEADS.UPDATE(task.lead.id), payload);
    return data?.data || data;
  };

  const handleLeadEditSuccess = (updatedLead) => {
    if (!updatedLead) return;
    const normalizedLead = {
      ...(task?.lead || {}),
      ...updatedLead,
    };
    const updatedAt = new Date().toISOString();
    const updatedTask = {
      ...task,
      lead: normalizedLead,
      updatedAt,
    };
    updateTask(updatedTask);
    setSelectedTask?.((prev) => ({
      ...prev,
      lead: normalizedLead,
      updatedAt,
    }));
    setShowLeadEditModal(false);
    onRefetchCard?.();
  };

  // Deletar anexo de uma tarefa em edição
  const handleDeleteAttachment = async (t, att) => {
    const cardId = t.cardId || t.card_id || t.CardId || task?.id;
    const attId = att.id || att.objectName || att.gcsKey;
    if (!attId) return;

    const ok = window.confirm(
      `Remover o anexo "${
        att.originalName || att.filename || att.name || "arquivo"
      }"?`
    );
    if (!ok) return;

    try {
      await axios.delete(CRM.TASK_ATTACHMENT_URL(cardId, t.id, attId));
      setTasks((prev) =>
        (prev || []).map((it) =>
          it.id === t.id
            ? {
                ...it,
                attachments: (it.attachments || []).filter(
                  (a) => (a.id || a.objectName || a.gcsKey) !== attId
                ),
              }
            : it
        )
      );
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao excluir anexo:", e);
      alert("Não foi possível excluir o anexo.");
    }
  };

  // ===== Criar TAREFA =====
  const uploadAttachmentsImmediately = async (files = []) => {
    if (!Array.isArray(files) || files.length === 0) return;
    if (uploadingAttachments || !task?.id) return;

    setUploadingAttachments(true);
    setAttachmentsUploadPct(0);

    const fd = new FormData();
    files.forEach((file) => fd.append("attachments", file));

    try {
      await axios.post(CRM.CARD_ATTACHMENTS(task.id), fd, {
        headers: { "Content-Type": undefined },
        onUploadProgress: (e) => {
          if (e?.total) {
            const pct = Math.round((e.loaded * 100) / e.total);
            setAttachmentsUploadPct(pct);
          }
        },
      });
      setActionFiles([]);
      await onRefetchCard?.(task?.id);
    } catch (err) {
      console.error("Erro ao enviar anexos:", err);
      window.alert("Não foi possível enviar o anexo.");
    } finally {
      setUploadingAttachments(false);
      setAttachmentsUploadPct(0);
    }
  };

  const handleAttachmentPickerChange = (files) => {
    setActionFiles(files || []);
    if ((files || []).length) {
      uploadAttachmentsImmediately(files);
    }
  };

  const cardAttachments = task?.attachments || [];

  const openCardAttachment = async (att) => {
    const key = att.id || att.objectName || att.gcsKey || att.filename;
    if (!task?.id || !key) return;

    setCardAttachmentLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await axios.get(
        CRM.CARD_ATTACHMENT_SIGNED_URL(task.id, att.id || key),
        { params: { ttl: 5 * 60 * 1000 } }
      );
      const url = data?.data?.url || data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Erro ao abrir anexo do card:", err);
      window.alert("Não foi possível abrir o anexo.");
    } finally {
      setCardAttachmentLoading((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const handleDeleteCardAttachment = async (att) => {
    if (!task?.id) return;
    const attId = att?.id;
    if (!attId) return;
    const name = att.originalName || att.filename || att.objectName || "anexo";
    const ok = window.confirm(`Remover o anexo "${name}" do card?`);
    if (!ok) return;

    const key =
      att.id ||
      att.objectName ||
      att.gcsKey ||
      att.filename ||
      att.originalName;
    setRemovingAttachmentIds((prev) => ({ ...prev, [key]: true }));

    try {
      await axios.delete(CRM.CARD_ATTACHMENT_DELETE(task.id, attId));
      await onRefetchCard?.(task.id);
    } catch (err) {
      console.error("Erro ao remover anexo do card:", err);
      window.alert("Não foi possível remover o anexo.");
    } finally {
      setRemovingAttachmentIds((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const CardAttachmentsList = ({ className = "" }) => {
    if (!cardAttachments.length) return null;
    return (
      <div className={className}>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
          Anexos do card
        </div>
        <div className="flex flex-wrap gap-2">
          {cardAttachments.map((att) => {
            const key =
              att.id ||
              att.objectName ||
              att.gcsKey ||
              att.filename ||
              att.originalName;
            const name =
              att.originalName ||
              att.filename ||
              att.name ||
              att.objectName ||
              "anexo";
            const loadingOpen = Boolean(cardAttachmentLoading[key]);
            const removing = Boolean(removingAttachmentIds[key]);
            return (
              <div
                key={key}
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[13px] transition"
              >
                <button
                  type="button"
                  onClick={() => openCardAttachment(att)}
                  disabled={loadingOpen || removing}
                  className={`flex items-center gap-1.5 text-left font-medium tracking-tight ${
                    loadingOpen || removing
                      ? "text-gray-400 cursor-wait"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  <HiPaperClip className="h-4 w-4" />
                  <span className="truncate max-w-[140px]">{name}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteCardAttachment(att)}
                  disabled={loadingOpen || removing}
                  className="flex aspect-square h-6 w-6 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:text-gray-300 disabled:border-gray-200"
                  title="Remover anexo"
                >
                  <HiOutlineTrash className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleSaveNextAction = async () => {
    const normalizedNote = toRichHtml(actionNoteHtml);
    const cleanHtml = stripVisualEmpty(normalizedNote);
    if (
      !cleanHtml &&
      !actionType &&
      !actionDueDate &&
      !actionDueTime &&
      actionFiles.length === 0
    )
      return;

    try {
      const dueDateISO = buildLocalISO(actionDueDate, actionDueTime);

      const basePayload = {
        type: actionType || null,
        note: cleanHtml || "",
        dueDate: dueDateISO || null,
        responsibleIds: Array.isArray(actionResponsibleIds)
          ? actionResponsibleIds.filter(Boolean)
          : [],
        responsibleId:
          Array.isArray(actionResponsibleIds) && actionResponsibleIds.length
            ? actionResponsibleIds[0]
            : null,
      };

      let response;

      if (actionFiles && actionFiles.length > 0) {
        setIsUploadingTask(true);
        setUploadPct(0);

        const fd = new FormData();
        Object.entries(basePayload).forEach(([k, v]) => {
          if (v !== undefined && v !== null) fd.append(k, v);
        });
        actionFiles.forEach((f) => fd.append("attachments", f));

        response = await axios.post(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`,
          fd,
          {
            headers: { "Content-Type": undefined },
            onUploadProgress: (e) => {
              if (e && e.total) {
                const pct = Math.round((e.loaded * 100) / e.total);
                setUploadPct(pct);
              }
            },
          }
        );
      } else {
        response = await axios.post(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`,
          basePayload,
          { headers: { "Content-Type": undefined } }
        );
      }

      const created =
        response?.data?.data ?? response?.data?.task ?? response?.data ?? null;

      if (created && created.id) {
        const responsiblesFromPayload =
          basePayload.responsibleIds?.map((id) => {
            const u = (availableUsers || []).find(
              (user) => String(user.id) === String(id)
            );
            return u ? { id: u.id, name: u.name, email: u.email } : { id };
          }) || [];

        const createdWithResponsibles = {
          ...created,
          responsibles: created?.responsibles ?? responsiblesFromPayload,
          responsibleId:
            created?.responsibleId ??
            basePayload.responsibleId ??
            responsiblesFromPayload[0]?.id ??
            null,
          responsible:
            created?.responsible ??
            responsiblesFromPayload[0] ??
            created?.responsibles?.[0] ??
            null,
        };

        setTasks((prev) =>
          sortTasksByDueDate([createdWithResponsibles, ...(prev || [])])
        );
      } else {
        await reloadTasks();
      }

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);

      setActionType("");
      setActionNoteHtml("");
      setActionDueDate("");
      setActionDueTime("");
      setActionResponsibleIds(extractResponsibleIdsFromTask(task));
      setActionFiles([]);
      setIsEditingTask(false);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao salvar ação:", err);
      window.alert("Não foi possível salvar a tarefa.");
    } finally {
      setIsUploadingTask(false);
      setUploadPct(0);
    }
  };

  // continua igual: só atualiza o estado local enquanto digita
  const handleChangeCustomField = (field, value) => {
    const key = field.key || field.fieldKey || field.name;
    if (!key) return;
    setCustomFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    setCustomFieldsDirty(true);
  };

  // função genérica que salva TODOS os campos, permitindo override de alguns valores
  // função genérica que salva TODOS os campos, permitindo override de alguns valores
  const saveCustomFields = async (overrideValues = {}) => {
    if (!task?.id) return;

    try {
      setSavingCustomFields(true);

      // 🔴 ANTES estava: task?.fields || task?.customFields || []
      const fields =
        task?.customFieldsSchema ||
        task?.fields ||
        task?.customFields ||
        (task?.customFieldValues || []).map((cv) => cv.field).filter(Boolean);

      if (!fields || !fields.length) {
        setSavingCustomFields(false);
        return;
      }

      // junta o estado atual com o valor que acabou de sair do blur
      const mergedValues = {
        ...customFieldValues,
        ...overrideValues,
      };

      const payloadValues = fields.map((field) => {
        const key =
          field.key || field.fieldKey || field.name || String(field.id);

        return {
          customFieldId: field.id,
          value: mergedValues[key],
        };
      });

      await axios.put(`${CRM.UPDATE_CARD}/${task.id}`, {
        customFields: payloadValues,
      });

      const updatedAt = new Date().toISOString();

      // ❗ Não sobrescreve customFieldValues aqui para não quebrar o formato
      updateTask({
        ...task,
        updatedAt,
      });

      setSelectedTask?.((prev) => ({
        ...(prev || {}),
        updatedAt,
      }));

      setCustomFieldsDirty(false);
    } catch (err) {
      console.error("Erro ao salvar propriedades do card:", err);
      alert("Não foi possível salvar as propriedades do card.");
    } finally {
      setSavingCustomFields(false);
    }
  };

  // ainda existe (se quiser usar em outro lugar), mas agora só delega
  const handleSaveCustomFields = () => saveCustomFields();

  // NOVO: será chamado no onBlur de cada campo
  const handleBlurCustomField = (field, value) => {
    const key = field.key || field.fieldKey || field.name || String(field.id);
    if (!key) return;

    // chama o save passando só o campo que mudou
    saveCustomFields({ [key]: value });
  };

  // ===== Editar / concluir / excluir TAREFA =====
  const startEditTask = (t) => {
    setEditingTaskId(t.id);
    setEditType(t.type || "");
    setEditNoteHtml(t.note || "");
    setEditDueDate(t?.dueDate ? String(t.dueDate).slice(0, 10) : "");
    setEditDueTime(t?.dueDate ? dateToHHMM(new Date(t.dueDate)) : "");
    setEditResponsibleIds(extractResponsibleIdsFromTask(t));
    setTaskBeingEdited(t);
    setEditModalOpen(true);
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditType("");
    setEditNoteHtml("");
    setEditDueDate("");
    setEditDueTime("");
    setEditResponsibleIds([]);
    invalidateStats(task?.id);
  };

  const saveEditTask = async (t) => {
    try {
      const dueISO = buildLocalISO(editDueDate, editDueTime);
      const htmlNow = toRichHtml(editNoteHtml || "");
      const cleanHtml = stripVisualEmpty(htmlNow);

      const responsibleIdsPayload = Array.isArray(editResponsibleIds)
        ? editResponsibleIds.filter(Boolean)
        : [];

      const basePayload = {
        type: editType || null,
        note: cleanHtml,
        dueDate: dueISO || null,
        responsibleId: responsibleIdsPayload[0] || null,
        responsibleIds: responsibleIdsPayload,
      };

      let response;

      if (editFiles && editFiles.length > 0) {
        const fd = new FormData();

        Object.entries(basePayload).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            fd.append(k, v);
          }
        });

        editFiles.forEach((f) => fd.append("attachments", f));

        response = await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, fd, {
          headers: { "Content-Type": undefined }, // deixa o axios/browser cuidar
        });
      } else {
        response = await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, basePayload);
      }

      const updatedTask = response?.data?.data ??
        response?.data?.task ?? { ...t, ...basePayload };

      setTasks((prev) =>
        sortTasksByDueDate(
          (prev || []).map((it) =>
            it.id === t.id ? { ...it, ...updatedTask } : it
          )
        )
      );

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));

      await onRefetchCard?.(task?.id);
      setEditFiles([]);
      cancelEditTask();
    } catch (err) {
      console.error("Erro ao editar tarefa:", err);
      window.alert("Não foi possível salvar as alterações.");
    }
  };

  const registerTaskHistory = async (type, t) => {
    try {
      await axios.post(`${CRM.ADD_CARD_HISTORY}/${task?.id}/history`, {
        type,
        taskId: t.id,
        text:
          type === "task_completed"
            ? "Tarefa marcada como concluída."
            : "Tarefa reaberta.",
      });
    } catch (e) {}
  };

  const handleToggleTaskStatus = async (t, shouldComplete) => {
    try {
      if (shouldComplete) {
        await axios.post(`${CRM.UPDATE_TASK}/${t.id}/complete`);
        setTasks((prev) =>
          sortTasksByDueDate(
            (prev || []).map((it) =>
              it.id === t.id
                ? {
                    ...it,
                    status: "done",
                    completedAt: new Date().toISOString(),
                  }
                : it
            )
          )
        );
        await registerTaskHistory("task_completed", t);
      } else {
        await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, {
          status: "open",
          completedAt: null,
        });
        setTasks((prev) =>
          sortTasksByDueDate(
            (prev || []).map((it) =>
              it.id === t.id ? { ...it, status: "open", completedAt: null } : it
            )
          )
        );
        await registerTaskHistory("task_reopened", t);
      }
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
      window.alert("Não foi possível atualizar o status da tarefa.");
    }
  };

  const handleDeleteTask = async (t) => {
    try {
      const ok = window.confirm("Deseja excluir esta tarefa?");
      if (!ok) return;
      await axios.delete(`${CRM.DELETE_TASK}/${t.id}`);
      setTasks((prev) => (prev || []).filter((it) => it.id !== t.id));
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao excluir tarefa:", err);
      window.alert("Não foi possível excluir a tarefa.");
    }
  };

  // ===== Comentários =====
  const handleAddComment = async () => {
    const normalized = toRichHtml(commentHtml);
    const clean = stripVisualEmpty(normalized);
    if (!clean) return;
    try {
      const response = await axios.post(
        `${CRM.ADD_CARD_HISTORY}/${task?.id}/history`,
        { text: clean, type: "comment" }
      );
      const created = response?.data?.data;
      if (created && (created.type || "comment") === "comment") {
        const enriched = {
          ...created,
          userId: created.userId || created.user_id || currentUser?.id || null,
          User:
            created.User ||
            (currentUser
              ? { id: currentUser.id, name: currentUser.name }
              : undefined),
        };
        setHistory((prev) => [enriched, ...(prev || [])]);
      }
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      setCommentHtml("");
    } catch (err) {
      console.error("Erro ao adicionar comentário:", err);
    }
  };

  // ===== Editar/Excluir Comentário =====
  const apiUpdateComment = (commentId, payload) => {
    const tries = [
      () =>
        axios.put(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`,
          payload
        ),
      () =>
        axios.patch(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`,
          payload
        ),
    ];
    return tries.reduce(
      (p, fn) => p.catch(fn),
      Promise.reject(new Error("try next"))
    );
  };

  const apiDeleteComment = (commentId) => {
    const tries = [
      () =>
        axios.delete(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`
        ),
    ];
    return tries.reduce(
      (p, fn) => p.catch(fn),
      Promise.reject(new Error("try next"))
    );
  };

  const handleSaveEditedComment = async (h) => {
    const normalized = toRichHtml(editCommentHtml);
    const clean = stripVisualEmpty(normalized);
    if (!clean) {
      if (
        !window.confirm("O texto ficou vazio. Deseja excluir este comentário?")
      )
        return;
      return handleDeleteComment(h);
    }
    try {
      await apiUpdateComment(h.id, {
        text: clean,
        type: "comment",
        editedAt: new Date().toISOString(),
        isEdited: true,
      });

      setHistory((prev) =>
        (prev || []).map((it) =>
          it.id === h.id
            ? {
                ...it,
                text: clean,
                editedAt: new Date().toISOString(),
                isEdited: true,
              }
            : it
        )
      );
      setEditingCommentId(null);
      setEditCommentHtml("");

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao editar comentário:", e);
      alert("Não foi possível salvar a edição do comentário.");
    }
  };

  const handleDeleteComment = async (h) => {
    const ok = window.confirm("Excluir este comentário?");
    if (!ok) return;
    try {
      await apiDeleteComment(h.id);
      setHistory((prev) => (prev || []).filter((it) => it.id !== h.id));

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao excluir comentário:", e);
      alert("Não foi possível excluir o comentário.");
    }
  };

  const handleGoToStage = async (targetStageId) => {
    if (!targetStageId || targetStageId === task.stageId) return;
    try {
      const requiresFields = await onMoveStage?.(task.id, targetStageId);
      if (requiresFields) return;
      const updatedAt = new Date().toISOString();
      setSelectedTask?.((prev) => ({
        ...prev,
        stageId: targetStageId,
        updatedAt,
      }));
    } catch (e) {
      console.error("Erro ao mover stage via timeline:", e);
    }
  };

  const lastTask =
    Array.isArray(tasks) && tasks.length
      ? [...tasks].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )[0]
      : null;

  const commentOnlyHistory = Array.isArray(history)
    ? history.filter((h) => (h?.type || "comment") === "comment")
    : [];

  const formatTaskResponsibleLabel = (task) => {
    if (!task) return null;
    const list = Array.isArray(task?.responsibles)
      ? task.responsibles
          .map((r) => r?.name || getUserNameById?.(r?.id))
          .filter(Boolean)
      : [];
    if (list.length) return list.join(", ");

    if (task?.responsible?.name) return task.responsible.name;
    if (task?.responsibleId) return String(task.responsibleId);
    return null;
  };

  const isCommentOwner = (h) => {
    const uid = h.userId || h.user_id || h.User?.id;
    return currentUser?.id && String(uid) === String(currentUser.id);
  };

  // ===== Valor da venda (visual + edit) =====
  const [isEditingDeal, setIsEditingDeal] = useState(false);
  const [dealValue, setDealValue] = useState(
    Number(task?.dealValue ?? task?.value ?? task?.amount) || 0
  );
  const [dealInput, setDealInput] = useState(
    (
      Number(task?.dealValue ?? task?.value ?? task?.amount) || 0
    ).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  );
  const [leadBudgets, setLeadBudgets] = useState([]);
  const [leadBudgetsLoading, setLeadBudgetsLoading] = useState(false);
  const [leadBudgetsError, setLeadBudgetsError] = useState("");
  const [budgetLinkLoadingId, setBudgetLinkLoadingId] = useState(null);
  const [budgetLinkError, setBudgetLinkError] = useState("");
  const [copiedBudgetId, setCopiedBudgetId] = useState(null);

  useEffect(() => {
    const v = Number(task?.dealValue ?? task?.value ?? task?.amount) || 0;
    setDealValue(v);
    setDealInput(
      v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
    setActionResponsibleIds(extractResponsibleIdsFromTask(task));
  }, [task?.id]);

  const formatCurrencyValue = (value = 0) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);

  const onEditDeal = () => {
    if (hasLinkedBudget && linkedBudgetValue > 0) {
      setDealValue(linkedBudgetValue);
      setDealInput(formatCurrencyValue(linkedBudgetValue));
    }
    setIsEditingDeal(true);
  };
  const onCancelDeal = () => {
    setIsEditingDeal(false);
    setDealInput(
      dealValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };
  const onlyDigits = (s) => (s || "").replace(/\D/g, "");
  const onChangeDeal = (e) => {
    const digits = onlyDigits(e.target.value);
    const money = Number(digits || "0") / 100 || 0;
    setDealInput(
      money.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };
  const toNumberFromMasked = (s) => {
    const cents = Number(onlyDigits(s));
    if (!Number.isFinite(cents)) return 0;
    return cents / 100;
  };
  const onSaveDeal = async () => {
    const numeric = toNumberFromMasked(dealInput);
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, { dealValue: numeric });
      setDealValue(numeric);
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, dealValue: numeric, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, dealValue: numeric, updatedAt }));
      setIsEditingDeal(false);
    } catch (e) {
      console.error("Erro ao salvar valor da venda:", e);
      window.alert("Não foi possível salvar o valor da venda.");
    }
  };

  const openBudgetDetail = (budgetId) => {
    if (!budgetId) return;
    window.open(`/budget?budgetId=${encodeURIComponent(budgetId)}`, "_blank");
  };

  const applyBudgetValue = (budget) => {
    const totalValue = Number(budget?.totalValue ?? budget?.total ?? 0) || 0;
    setDealValue(totalValue);
    setDealInput(formatCurrencyValue(totalValue));
  };

  const handleCopyBudgetReference = (budget) => {
    const reference = budget?.reference || budget?.id;
    if (!reference) return;
    navigator.clipboard?.writeText(reference);
    setCopiedBudgetId(budget.id);
    setTimeout(() => setCopiedBudgetId(null), 1200);
  };

  const handleLinkBudget = async (budget) => {
    if (!task?.id) return;
    const targetBudgetId = budget?.id || null;
    setBudgetLinkError("");
    setBudgetLinkLoadingId(targetBudgetId || "unlink");
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task.id}`, {
        budgetId: targetBudgetId,
      });
      const updatedAt = new Date().toISOString();
      const linkedValue =
        budget &&
        Number(budget.totalValue ?? budget.subtotal ?? budget?.total ?? 0);
      if (budget && Number.isFinite(linkedValue)) {
        setDealValue(linkedValue);
        setDealInput(formatCurrencyValue(linkedValue));
      }
      const updatedBudget = budget ? { ...budget } : null;
      const updatedTask = {
        ...task,
        budget: updatedBudget,
        updatedAt,
      };
      updateTask?.(updatedTask);
      setSelectedTask?.((prev) => ({
        ...prev,
        budget: updatedBudget,
        updatedAt,
      }));
    } catch (error) {
      console.error("Erro ao atualizar o orçamento vinculado:", error);
      setBudgetLinkError(
        error?.response?.data?.message ||
          error?.message ||
          "Não foi possível atualizar o orçamento vinculado."
      );
    } finally {
      setBudgetLinkLoadingId(null);
    }
  };

  const leadIdForBudgets =
    task?.lead?.id ?? task?.leadId ?? (task?.lead && task.lead?.leadId) ?? null;

  useEffect(() => {
    let active = true;
    if (!isEditingDeal || !leadIdForBudgets) {
      setLeadBudgets([]);
      setLeadBudgetsError("");
      return () => {
        active = false;
      };
    }

    const loadLeadBudgets = async () => {
      setLeadBudgetsLoading(true);
      setLeadBudgetsError("");
      try {
        const response = await axios.get(BUDGETS.LIST, {
          params: { leadId: leadIdForBudgets, limit: 6, page: 1 },
        });
        if (!active) return;
        const result = response?.data?.data?.budgets ?? [];
        setLeadBudgets(result);
      } catch (error) {
        if (!active) return;
        setLeadBudgetsError(
          error?.response?.data?.message ||
            "Não foi possível carregar os orçamentos deste cliente."
        );
      } finally {
        if (active) setLeadBudgetsLoading(false);
      }
    };

    loadLeadBudgets();
    return () => {
      active = false;
    };
  }, [isEditingDeal, task?.lead?.id, leadIdForBudgets]);

  const renderLeadBudgetsPanel = () => {
    if (!isEditingDeal) return null;

    const linkingBudget = budgetLinkLoadingId === "unlink";

    return (
      <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">
              Orçamentos deste cliente
            </p>
          </div>
          <Button
            size="xs"
            color="light"
            onClick={() =>
              window.open(`/budgets?leadId=${leadIdForBudgets}`, "_blank")
            }
            disabled={!leadIdForBudgets}
          >
            Ver tudo
          </Button>
        </div>
        {budgetLinkError && (
          <div className="text-sm text-rose-600">{budgetLinkError}</div>
        )}
        <div>
          {linkedBudget ? (
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Orçamento vinculado
                  </p>
                  <p className="text-base font-semibold text-slate-900">
                    {linkedBudget.reference || linkedBudget.id}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {formatCurrencyValue(
                      Number(
                        linkedBudget.totalValue ?? linkedBudget.subtotal ?? 0
                      )
                    )}
                    {linkedBudget.status ? ` • ${linkedBudget.status}` : ""}
                  </p>
                  {linkedBudget.createdAt && (
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Criado em{" "}
                      {moment(linkedBudget.createdAt).format("DD/MM/YYYY")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => openBudgetDetail(linkedBudget.id)}
                  >
                    Abrir
                  </Button>
                  <Button
                    size="xs"
                    color="failure"
                    onClick={() => handleLinkBudget(null)}
                    disabled={linkingBudget}
                  >
                    {linkingBudget ? "Desvinculando..." : "Desvincular"}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-500">
              Nenhum orçamento vinculado ao card.
            </div>
          )}
        </div>
        {leadBudgetsLoading ? (
          <div className="text-sm text-slate-500">Carregando orçamentos…</div>
        ) : leadBudgetsError ? (
          <div className="text-sm text-rose-600">{leadBudgetsError}</div>
        ) : !leadBudgets.length ? (
          <div className="text-sm text-slate-500">
            Ainda não há orçamentos para este cliente.
          </div>
        ) : (
          <div className="grid gap-3">
            {leadBudgets.map((budget) => {
              const totalValue =
                Number(budget.totalValue ?? budget.total ?? 0) || 0;
              const createdAt = budget.createdAt
                ? moment(budget.createdAt).format("DD/MM/YYYY")
                : "-";
              const isLinked = linkedBudget && linkedBudget.id === budget.id;
              return (
                <div
                  key={budget.id}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                        Referência
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {budget.reference || budget.id}
                      </p>
                    </div>
                    <span className="text-xs text-slate-600">
                      {formatCurrencyValue(totalValue)}
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Criado em {createdAt}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="xs"
                      color={isLinked ? "success" : "light"}
                      onClick={() => handleLinkBudget(budget)}
                      disabled={isLinked || budgetLinkLoadingId === budget.id}
                    >
                      {budgetLinkLoadingId === budget.id
                        ? "Vinculando..."
                        : isLinked
                        ? "Vinculado"
                        : "Vincular"}
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => openBudgetDetail(budget.id)}
                    >
                      Abrir
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // menu de ações: controle do aberto
  const activitiesByMonth = useMemo(() => {
    if (!activities) return {};

    return activities.reduce((acc, item) => {
      const monthLabel = moment(item.createdAt).format("MMMM YYYY"); // ex: Dezembro 2025

      if (!acc[monthLabel]) acc[monthLabel] = [];
      acc[monthLabel].push(item);

      return acc;
    }, {});
  }, [activities]);

  const upcomingTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    const now = Date.now();
    return tasks
      .filter(
        (task) =>
          task?.dueDate &&
          Date.parse(task.dueDate) > now &&
          task.status !== "done"
      )
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [tasks]);

  const openTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return [...tasks]
      .filter((t) => t?.status !== "done")
      .sort((a, b) => {
        const aValue = parseDueTimestamp(a);
        const bValue = parseDueTimestamp(b);
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        return aValue - bValue;
      });
  }, [tasks]);

  const completedTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return [...tasks]
      .filter((t) => t?.status === "done")
      .sort((a, b) => {
        const aValue =
          parseTimestampValue(a?.completedAt) ?? parseDueTimestamp(a);
        const bValue =
          parseTimestampValue(b?.completedAt) ?? parseDueTimestamp(b);
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        return bValue - aValue;
      });
  }, [tasks]);

  return (
    <Modal
      show={true}
      onClose={handleClose}
      size="7xl"
      className="
    !p-0
    [&>div]:w-screen
    [&>div]:h-screen
    [&>div]:max-w-none
  "
    >
      {/* ===== MOBILE ===== */}
      <div className="lg:hidden fixed inset-0 bg-white flex flex-col">
        {/* HEADER MOBILE */}
        <div className="sticky top-0 z-20 border-b bg-white px-3 py-2 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold truncate">
            {task?.lead?.name || "Cliente"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDeleteCard}
              disabled={deleting}
              title="Excluir card"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs ${
                deleting
                  ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              }`}
            >
              <HiOutlineTrash size={14} />
              {deleting ? "Excl…" : "Excluir"}
            </button>
            <Button size="xs" color="light" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        </div>

        {/* ETAPA MOBILE */}
        <div className="px-3 py-2 bg-gray-50 border-b">
          <label className="text-xs text-gray-600 block">Etapa</label>
          <Select
            value={task?.stageId}
            onChange={(e) => handleGoToStage(e.target.value)}
          >
            {(stages || []).map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="px-3 py-3 bg-white border-b">
          <AttachmentPicker
            compact
            label="Anexos"
            files={actionFiles}
            onChange={handleAttachmentPickerChange}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          />
          {uploadingAttachments && (
            <div className="mt-2 text-[11px] text-gray-500">
              Enviando anexos... {attachmentsUploadPct}%
            </div>
          )}
          <CardAttachmentsList className="mt-3" />
        </div>

        {/* TABS MOBILE */}
        <div className="sticky top-[94px] z-10 bg-white border-b">
          <div className="flex overflow-x-auto no-scrollbar">
            {[
              { k: "detalhes", label: "Detalhes" },
              {
                k: "atividades",
                label: `Atividades (${activities?.length})`,
              },
              { k: "tarefas", label: `Tarefas (${tasks?.length || 0})` },
              {
                k: "comentarios",
                label: `Observações (${commentOnlyHistory.length})`,
              },
            ].map((tab) => (
              <button
                key={tab.k}
                onClick={() => setMobileTab(tab.k)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                  mobileTab === tab.k
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* BODY MOBILE */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {mobileTab === "detalhes" && (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Detalhes do cliente
                    </h4>
                    <p className="text-[11px] text-gray-500">
                      Informações do contato vinculado ao card
                    </p>
                  </div>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setShowLeadEditModal(true)}
                    disabled={!task?.lead?.id}
                  >
                    <HiOutlinePencil className="mr-1" /> Editar
                  </Button>
                </div>

                <div className="space-y-3 text-sm text-gray-700">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                      Informações Gerais
                    </div>
                    <div className="mt-1 text-base font-semibold text-slate-900">
                      {customerResponsible || "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px] text-gray-600">
                    {customerEmail && (
                      <Badge
                        color="gray"
                        className="px-3 py-1 rounded-full text-xs"
                      >
                        <div className="flex items-center gap-1">
                          <HiMail className="text-sm" />
                          <span className="truncate">
                            {customerEmail || "Sem e-mail"}
                          </span>
                        </div>
                      </Badge>
                    )}

                    <Badge
                      color="gray"
                      className="px-3 py-1 rounded-full text-xs flex items-center gap-1"
                    >
                      <div className="flex items-center gap-1">
                        <HiPhone className="text-sm" />
                        <span className="truncate">
                          {formatPhoneNumber(customerPhone) || "Sem telefone"}
                        </span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {customerEmail && (
                      <Button
                        size="xs"
                        color="light"
                        disabled={!customerEmail}
                        onClick={() =>
                          customerEmail &&
                          window.open(`mailto:${customerEmail}`, "_blank")
                        }
                      >
                        <HiMail className="mr-1" /> E-mail
                      </Button>
                    )}

                    <Button
                      size="xs"
                      color="light"
                      disabled={!customerPhone}
                      onClick={() =>
                        customerPhone &&
                        window.open(
                          `tel:${customerPhone.replace(/\D/g, "")}`,
                          "_blank"
                        )
                      }
                    >
                      <HiPhone className="mr-1" /> Ligar
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      disabled={!customerPhone}
                      onClick={() => {
                        if (!customerPhone) return;
                        const phone = customerPhone.replace(/\D/g, "");
                        window.open(`https://wa.me/${phone}`, "_blank");
                      }}
                    >
                      <HiChatAlt2 className="mr-1" /> WhatsApp
                    </Button>
                  </div>
                  {customerDocument && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="font-semibold text-gray-800">
                        Documento:
                      </span>
                      <span>{formatDocument(customerDocument)}</span>
                      <ReceitaTooltip cnpj={customerDocument} />
                    </div>
                  )}
                </div>
              </div>

              {showTagPicker && canManageTags && (
                <TagPicker
                  companyId={companyIdForTags}
                  funnelId={funnelIdForTags}
                  cardId={task?.id}
                  currentTag={currentTag}
                  onAssigned={(t) => {
                    handleAssignTag(t);
                    setShowTagPicker(false);
                  }}
                  onClose={() => setShowTagPicker(false)}
                />
              )}
              {showTagPicker && !canManageTags && (
                <div className="text-xs text-red-600 mt-2">
                  Informe o funil/etapa para listar as etiquetas corretas.
                </div>
              )}

              {/* ===== Etapa do funil (visual premium) ===== */}
              {(() => {
                const current =
                  (stages || []).find(
                    (s) => String(s.id) === String(task?.stageId)
                  ) || {};
                const pct = Number.isFinite(Number(current?.probability))
                  ? Number(current.probability)
                  : 0;
                const color = current?.color || "#9ca3af";

                return (
                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 leading-tight">
                            Etapa do funil
                          </div>
                          <div className="text-[11px] text-gray-500 leading-tight truncate">
                            {current?.name
                              ? `Atual: ${current.name}`
                              : "Selecione uma etapa"}
                          </div>
                        </div>
                      </div>

                      {/* Chip de probabilidade */}
                      <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] border bg-gray-50 text-gray-700 border-gray-200">
                        Probabilidade:{" "}
                        <span className="font-semibold">{pct}%</span>
                      </span>
                    </div>

                    {/* Select estilizado */}
                    <div className="mt-3 relative">
                      <Select
                        value={task?.stageId}
                        onChange={(e) => handleGoToStage(e.target.value)}
                        className="h-11 rounded-xl border-gray-200 focus:ring-2 focus:ring-gray-900/10"
                      >
                        {(stages || []).map((st) => (
                          <option key={st.id} value={st.id}>
                            {st.name}{" "}
                            {Number.isFinite(+st.probability)
                              ? `(${st.probability}%)`
                              : ""}
                          </option>
                        ))}
                      </Select>
                    </div>

                    {/* Ajuda / status */}
                    <div className="mt-2 flex items-center justify-between">
                      {current?.isWin && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Marcada como Ganho
                        </span>
                      )}
                      {current?.isLost && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-rose-50 text-rose-700 border border-rose-200">
                          Marcada como Perdido
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Valor da venda (compacto) */}
              <div className="rounded-lg border border-gray-600 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 p-3 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 border border-white/20">
                      <HiCurrencyDollar size={18} />
                    </div>
                    <span className="text-xs uppercase tracking-wide opacity-90">
                      Valor da venda
                    </span>
                  </div>
                  {!isEditingDeal && (
                    <button
                      type="button"
                      onClick={onEditDeal}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs"
                      title="Editar valor"
                    >
                      <HiOutlinePencil size={16} /> Editar
                    </button>
                  )}
                </div>
                {!isEditingDeal ? (
                  hasLinkedBudget ? (
                    <div className="mt-2 space-y-1">
                      <div className="text-2xl font-semibold">
                        {formatCurrencyValue(linkedBudgetValue)}
                      </div>
                      <p className="text-xs opacity-80">
                        Orçamento vinculado:
                        <span className="ml-1 font-semibold text-white">
                          {linkedBudget.reference || linkedBudget.id}
                        </span>
                      </p>
                      <p className="text-xs opacity-60">
                        Enquanto não editar, o valor assume o orçamento
                        vinculado.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-2xl font-semibold">
                        {dealValue.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </div>
                      <p className="mt-1 text-xs opacity-85">
                        Valor previsto/fechado desta oportunidade.
                      </p>
                    </div>
                  )
                ) : (
                  <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <TextInput
                      value={dealInput}
                      onChange={onChangeDeal}
                      placeholder="R$ 0,00"
                      aria-label="Valor da venda"
                      className="bg-white text-gray-900 rounded-md"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" color="light" onClick={onSaveDeal}>
                        Salvar
                      </Button>
                      <Button size="sm" color="light" onClick={onCancelDeal}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
                {renderLeadBudgetsPanel()}
              </div>

              {/* TAG */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-800">
                    Etiqueta do card
                  </h5>
                  <Button
                    size="xs"
                    color="light"
                    onClick={openTagPicker}
                    disabled={!canManageTags}
                  >
                    Gerenciar etiquetas
                  </Button>
                </div>

                {currentTag ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border"
                      style={{
                        background: `${currentTag.color || "#6b7280"}20`,
                        color: currentTag.color || "#6b7280",
                        borderColor: currentTag.color || "#6b7280",
                      }}
                    >
                      <span
                        className="inline-block h-4 w-6 rounded"
                        style={{
                          background: currentTag.color || "#6b7280",
                        }}
                      />
                      {currentTag.name}
                    </span>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleAssignTag(null)}
                      title="Remover etiqueta"
                    >
                      Remover
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem etiqueta atribuída.
                  </div>
                )}
                {renderLeadBudgetsPanel()}
              </div>

              {/* Próxima ação (resumo) */}
            </>
          )}

          {mobileTab === "tarefas" && (
            <TaskListCompact
              tasks={tasks}
              loadingTasks={loadingTasks}
              editingTaskId={editingTaskId}
              editType={editType}
              editNoteHtml={editNoteHtml}
              editDueDate={editDueDate}
              editDueTime={editDueTime}
              editResponsibleIds={editResponsibleIds}
              setEditType={setEditType}
              setEditNoteHtml={setEditNoteHtml}
              setEditDueDate={setEditDueDate}
              setEditDueTime={setEditDueTime}
              setEditResponsibleIds={setEditResponsibleIds}
              labelMap={labelMap}
              getUserNameById={getUserNameById}
              userOptions={userOptions}
              loadingUsers={loadingUsers}
              startEditTask={startEditTask}
              saveEditTask={saveEditTask}
              cancelEditTask={cancelEditTask}
              handleToggleTaskStatus={handleToggleTaskStatus}
              handleDeleteTask={handleDeleteTask}
              onDeleteAttachment={handleDeleteAttachment}
              onConfirmComplete={openConfirmComplete}
            />
          )}

          {mobileTab === "comentarios" && (
            <div className="space-y-3">
              {/* Editor simples primeiro */}
              <div className="rounded-xl border border-gray-200 bg-white">
                <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800">
                  Adicionar Observação
                </div>
                <div className="p-3">
                  {!commentRich ? (
                    <>
                      <textarea
                        className="w-full border rounded-md p-2 text-sm"
                        rows={5}
                        value={commentHtml.replace(/<[^>]+>/g, "")}
                        onChange={(e) => setCommentHtml(e.target.value)}
                        placeholder="Escreva seu comentário…"
                      />
                      <button
                        className="mt-2 text-xs text-gray-600 underline"
                        onClick={() => setCommentRich(true)}
                      >
                        Usar formatação avançada
                      </button>
                    </>
                  ) : (
                    <TiptapEditor
                      value={commentHtml}
                      onChange={setCommentHtml}
                      users={(availableUsers || []).map((u) => ({
                        id: u.id,
                        value: u.name || u.email || "Usuário",
                        email: u.email,
                        avatar: u.avatarUrl,
                      }))}
                      placeholder="Escreva seu comentário…"
                    />
                  )}
                  <div className="mt-2 flex justify-end gap-2">
                    <Button
                      size="sm"
                      color="light"
                      onClick={() =>
                        window.confirm("Limpar texto?") && setCommentHtml("")
                      }
                    >
                      Limpar
                    </Button>
                    <Button size="sm" onClick={handleAddComment}>
                      Enviar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Lista */}
              {commentOnlyHistory.length > 0 ? (
                <div className="space-y-2">
                  {commentOnlyHistory.map((h) => {
                    const name =
                      h.User?.name ||
                      getUserNameById?.(h.userId || h.user_id) ||
                      "Não atribuído";
                    const initials = name
                      .split(" ")
                      .map((s) => s[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase();
                    const edited = !!(h.editedAt || h.isEdited);
                    const ts = new Date(h.createdAt).toLocaleString("pt-BR");

                    return (
                      <div
                        key={h.id}
                        className="rounded-xl border border-gray-200 bg-white p-3"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 grid place-items-center text-[11px] font-semibold">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {name}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {ts}{" "}
                              {edited && (
                                <span className="ml-1 text-gray-400">
                                  (editado)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Ações do dono do comentário */}
                        {isCommentOwner(h) && (
                          <div className="mt-2 flex gap-3">
                            {editingCommentId !== h.id ? (
                              <>
                                <button
                                  className="text-[12px] text-blue-600 underline"
                                  onClick={() => {
                                    setEditingCommentId(h.id);
                                    setEditCommentHtml(h.text || "");
                                  }}
                                >
                                  Editar
                                </button>
                                <button
                                  className="text-[12px] text-red-600 underline"
                                  onClick={() => handleDeleteComment(h)}
                                >
                                  Excluir
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="text-[12px] text-gray-700 underline"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditCommentHtml("");
                                  }}
                                >
                                  Cancelar
                                </button>
                                <button
                                  className="text-[12px] text-blue-600 underline"
                                  onClick={() => handleSaveEditedComment(h)}
                                >
                                  Salvar
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* Conteúdo ou editor inline */}
                        {editingCommentId === h.id ? (
                          <TiptapEditor
                            value={editCommentHtml}
                            onChange={setEditCommentHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Edite sua observação..."
                          />
                        ) : (
                          <div
                            className="rich-text mt-2 text-gray-800 leading-relaxed text-sm"
                            dangerouslySetInnerHTML={{ __html: h.text || "" }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-gray-600">
                  Ainda não há observações.
                </div>
              )}
            </div>
          )}

          {mobileTab === "atividades" && (
            <div className="space-y-3">
              <div className="text-xs text-gray-500">
                Histórico técnico e de operações realizadas no card
              </div>

              <div className="rounded-2xl overflow-hidden">
                <div className="rounded-md">
                  {activities?.length > 0 ? (
                    <div className="max-h-[65vh] overflow-y-auto soft-scroll">
                      <div className="space-y-4">
                        {Object.keys(activitiesByMonth).map((month) => (
                          <section
                            key={month}
                            className="border border-gray-100 rounded-xl bg-white/60"
                          >
                            {/* Cabeçalho do mês */}
                            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                              <div className="text-xs font-semibold text-gray-700 tracking-wide uppercase">
                                {month.charAt(0).toUpperCase() + month.slice(1)}
                              </div>
                              <span className="text-[11px] text-gray-500">
                                {activitiesByMonth[month].length} atividade
                                {activitiesByMonth[month].length > 1 && "s"}
                              </span>
                            </div>

                            {/* Lista das atividades do mês */}
                            <ul className="divide-y divide-gray-100">
                              {activitiesByMonth[month].map((h, index) => {
                                const isTask = h.targetType === "TASK";
                                const hasDueDate =
                                  h.after && "dueDate" in (h.after || {});
                                const hasResponsible =
                                  h.after && h.after.responsibleId;

                                return (
                                  <li
                                    key={index}
                                    className="group px-3 py-2.5 hover:bg-gray-50/80 transition-colors text-xs"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="flex-1 min-w-0">
                                        {/* Linha de título: tipo de ação + card */}
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span
                                            className={
                                              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium " +
                                              getActionColor(h.action)
                                            }
                                          >
                                            {getActionLabel(h.action)}
                                          </span>
                                        </div>

                                        {/* Descrição principal */}
                                        <p className="mt-1 text-[11px] text-gray-700">
                                          {h?.description}
                                        </p>

                                        {/* Rodapé: data + usuário */}
                                        <div className="mt-1 text-[11px] text-gray-400">
                                          {moment(h?.createdAt).format(
                                            "DD/MM/YYYY [às] HH:mm"
                                          )}{" "}
                                          ·{" "}
                                          <span>
                                            por{" "}
                                            <span className="font-medium text-gray-600">
                                              {h?.actor?.name ||
                                                h?.actor?.email}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </section>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10 text-sm text-gray-600 flex flex-col items-center justify-center">
                      <span className="text-3xl">💬</span>
                      <span>Ainda não há atividades do card.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== DESKTOP ===== */}
      <div className="hidden lg:block overflow-y-hidden">
        <Modal.Header className="bg-gray-50 w-full">
          <div className="flex justify-between">
            <div className="flex items-center gap-5 justify-between">
              <h2 className="text-base font-semibold text-gray-800">
                {task?.lead?.name || "Cliente"}
              </h2>

              {/* TAG única do card */}
              <div className="flex items-center gap-2">
                {currentTag ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium border"
                      style={{
                        background: `${currentTag.color || "#6b7280"}20`,
                        color: currentTag.color || "#6b7280",
                        borderColor: currentTag.color || "#6b7280",
                      }}
                    >
                      <span
                        className="inline-block h-4 w-6 rounded"
                        style={{
                          background: currentTag.color || "#6b7280",
                        }}
                      />
                      {currentTag.name}
                    </span>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleAssignTag(null)}
                      title="Remover etiqueta"
                      className="py-1"
                    >
                      Remover
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem etiqueta atribuída.
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Button
                    size="sm"
                    color="light"
                    onClick={openTagPicker}
                    className="py-1"
                    disabled={!canManageTags}
                  >
                    <IoMdPricetags />
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-3">
              <Button
                size="sm"
                color="failure"
                onClick={handleDeleteCard}
                disabled={deleting}
                className="flex items-center gap-2"
              >
                <div className="flex items-center gap-2">
                  {deleting ? (
                    <span>
                      <Spinner size="sm" className="mr-2" />
                      Excluindo...
                    </span>
                  ) : (
                    <>
                      <HiOutlineTrash className="h-4 w-4" />
                      <span>Excluir card</span>
                    </>
                  )}
                </div>
              </Button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className=" h-[calc(85vh)] overflow-y-hidden">
          {/* Grid principal */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* ===== ESQ ===== */}
            <div
              className="lg:col-span-2 space-y-6 overflow-y-auto pr-3"
              style={{ maxHeight: "75vh" }}
            >
              {/* === DETALHES DO CLIENTE + TAG === */}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Detalhes do cliente
                    </h3>
                    <p className="text-xs text-gray-500">
                      Informações do contato associado ao card.
                    </p>
                  </div>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setShowLeadEditModal(true)}
                    disabled={!task?.lead?.id}
                  >
                    <div className="flex items-center">
                      <HiOutlinePencil className="mr-1" /> <span>Editar</span>
                    </div>
                  </Button>
                </div>

                <div className="space-y-4 text-sm text-gray-700">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                      Informações gerais
                    </div>
                    <div className="mt-1 text-base font-semibold text-slate-900">
                      {customerResponsible || "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                    {customerEmail && (
                      <Badge color="gray" className="px-4 py-1 rounded-full">
                        <div className="flex items-center gap-1">
                          <HiMail className="text-sm" />
                          <span className="truncate">
                            {customerEmail || "Sem e-mail"}
                          </span>
                        </div>
                      </Badge>
                    )}

                    <Badge color="gray" className="px-4 py-1 rounded-full">
                      <div className="flex items-center gap-1">
                        <HiPhone className="text-sm" />
                        <span className="truncate">
                          {formatPhoneNumber(customerPhone) || "Sem telefone"}
                        </span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {customerEmail && (
                      <Button
                        size="xs"
                        color="light"
                        disabled={!customerEmail}
                        onClick={() =>
                          customerEmail &&
                          window.open(`mailto:${customerEmail}`, "_blank")
                        }
                      >
                        <div className="flex items-center">
                          <HiMail className="mr-1" /> <span>E-mail</span>
                        </div>
                      </Button>
                    )}

                    <Button
                      size="xs"
                      color="light"
                      disabled={!customerPhone}
                      onClick={() =>
                        customerPhone &&
                        window.open(
                          `tel:${customerPhone.replace(/\D/g, "")}`,
                          "_blank"
                        )
                      }
                    >
                      <div className="flex items-center">
                        <HiPhone className="mr-1" /> <span>Ligar</span>
                      </div>
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      disabled={!customerPhone}
                      onClick={() => {
                        if (!customerPhone) return;
                        const phone = customerPhone.replace(/\D/g, "");
                        window.open(`https://wa.me/${phone}`, "_blank");
                      }}
                    >
                      <div className="flex items-center">
                        <HiChatAlt2 className="mr-1" /> <span>WhatsApp</span>
                      </div>
                    </Button>
                  </div>
                  {customerDocument && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="font-semibold text-gray-900">
                        Documento:
                      </span>
                      <span>{formatDocument(customerDocument)}</span>
                      <ReceitaTooltip cnpj={customerDocument} />
                    </div>
                  )}
                </div>

                {(() => {
                  const current =
                    (stages || []).find(
                      (s) => String(s.id) === String(task?.stageId)
                    ) || {};
                  const pct = Number.isFinite(Number(current?.probability))
                    ? Number(current.probability)
                    : 0;
                  const color = current?.color || "#9ca3af";

                  return (
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 leading-tight">
                              Etapa Atual:
                            </div>
                            <div className="text-[11px] text-gray-500 leading-tight truncate">
                              {current?.name
                                ? `Atual: ${current.name}`
                                : "Selecione uma etapa"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Select estilizado */}
                      <div className="mt-2 relative">
                        <Select
                          value={task?.stageId}
                          onChange={(e) => handleGoToStage(e.target.value)}
                          className="h-11 rounded-xl border-gray-200 focus:ring-2 focus:ring-gray-900/10"
                        >
                          {(stages || []).map((st) => (
                            <option key={st.id} value={st.id}>
                              {st.name}{" "}
                              {Number.isFinite(+st.probability)
                                ? `(${st.probability}%)`
                                : ""}
                            </option>
                          ))}
                        </Select>
                      </div>

                      {/* Ajuda / status */}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {current?.isWin && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Marcada como Ganho
                          </span>
                        )}
                        {current?.isLost && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-rose-50 text-rose-700 border border-rose-200">
                            Marcada como Perdido
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-4">
                  <AttachmentPicker
                    label="Anexos"
                    files={actionFiles}
                    onChange={handleAttachmentPickerChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  />
                  {uploadingAttachments && (
                    <div className="mt-2 text-xs text-gray-500">
                      Enviando anexos... {attachmentsUploadPct}%
                    </div>
                  )}
                  <CardAttachmentsList className="mt-3" />
                </div>

                {showTagPicker && canManageTags && (
                  <TagPicker
                    companyId={companyIdForTags}
                    funnelId={funnelIdForTags}
                    cardId={task?.id}
                    currentTag={currentTag}
                    onAssigned={(t) => {
                      handleAssignTag(t);
                      setShowTagPicker(false);
                    }}
                    onClose={() => setShowTagPicker(false)}
                  />
                )}
                {showTagPicker && !canManageTags && (
                  <div className="text-xs text-red-600 mt-2">
                    Informe o funil/etapa do card para acessar as etiquetas.
                  </div>
                )}
              </div>

              {/* Responsável do card */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">
                  Responsável do card
                </h4>
                {isEditingResponsible ? (
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="flex-1">
                      <AutoCompleteInput
                        label="Responsável"
                        name="card-responsible"
                        options={userOptions}
                        value={selectedResponsible || ""}
                        onChange={handleSaveResponsible}
                        isLoading={loadingUsers}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center mt-1 rounded-lg bg-gray-50 px-2 py-3 justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar size="sm" rounded />
                      <span className="text-sm text-gray-800 font-medium">
                        {task?.responsible?.name || "Não atribuído"}
                      </span>
                      {selectedResponsible && (
                        <button
                          className="p-0.5 text-sm rounded-full bg-gray-200 "
                          onClick={() => handleSaveResponsible({ value: null })}
                        >
                          <HiOutlineX />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => setIsEditingResponsible(true)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <HiOutlinePencil size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {leadId && (
                <div className="mt-6">
                  <LeadContactsPanel leadId={leadId} />
                </div>
              )}

              {/* Valor da venda (desktop) */}
              <div className="rounded-2xl border border-gray-600 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 p-4 shadow-sm text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 border border-white/20">
                      <HiCurrencyDollar size={18} />
                    </div>
                    <span className="text-xs uppercase tracking-wide opacity-90">
                      Valor da venda
                    </span>
                  </div>
                  {!isEditingDeal && (
                    <button
                      type="button"
                      onClick={onEditDeal}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs"
                      title="Editar valor"
                    >
                      <HiOutlinePencil size={16} />
                    </button>
                  )}
                </div>
                {!isEditingDeal ? (
                  hasLinkedBudget ? (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl md:text-2xl font-semibold leading-none tracking-tight">
                          {formatCurrencyValue(linkedBudgetValue)}
                        </span>
                      </div>
                      <p className="text-xs opacity-80">
                        Orçamento vinculado:
                        <span className="ml-1 font-semibold text-white">
                          {linkedBudget.reference || linkedBudget.id}
                        </span>
                      </p>
                      <p className="text-xs opacity-60">
                        Enquanto não editar, o valor assume o orçamento
                        vinculado.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl md:text-2xl font-semibold leading-none tracking-tight">
                          {dealValue.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </span>
                      </div>
                      <p className="mt-1 text-xs opacity-85">
                        Valor previsto/fechado desta oportunidade.
                      </p>
                    </div>
                  )
                ) : (
                  <div>
                    <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <TextInput
                        value={dealInput}
                        onChange={onChangeDeal}
                        placeholder="R$ 0,00"
                        aria-label="Valor da venda"
                        className="bg-white text-gray-900 rounded-md"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" color="light" onClick={onSaveDeal}>
                          Salvar
                        </Button>
                        <Button size="sm" color="light" onClick={onCancelDeal}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                    {renderLeadBudgetsPanel()}
                  </div>
                )}
              </div>

              {(task?.customFieldsSchema || []).length > 0 && (
                <div className="mt-4">
                  <CustomFieldsSection
                    fields={task?.customFieldsSchema || []}
                    values={customFieldValues}
                    onChange={handleChangeCustomField}
                    onBlurField={handleBlurCustomField} // ⬅️ IMPORTANTE
                  />
                </div>
              )}

              <div style={{ height: "80px" }}></div>
            </div>

            {/* ===== DIR – TABS CUSTOM (tarefas / adicionar / comentários / formulário) ===== */}
            <div
              className="lg:col-span-4 overflow-y-auto pr-3"
              style={{ maxHeight: "75vh" }}
            >
              <div className="rtabs rounded-xl bg-white overflow-hidden">
                <input
                  type="radio"
                  id="rtab-logs"
                  name="rtabs"
                  className="rtab-input"
                  defaultChecked
                />

                <input
                  type="radio"
                  id="rtab-tarefas"
                  name="rtabs"
                  className="rtab-input"
                />
                <input
                  type="radio"
                  id="rtab-coment"
                  name="rtabs"
                  className="rtab-input"
                />

                {/* NAV */}
                <div className="rtabs-nav flex items-center overflow-x-auto no-scrollbar ">
                  <label htmlFor="rtab-logs" className="rtab-btn">
                    <RxActivityLog className="inline-flex" fontSize={15} />{" "}
                    <span>
                      Atividades{" "}
                      <span className="text-xs">
                        ({activities?.length || 0})
                      </span>
                    </span>
                  </label>
                  <label htmlFor="rtab-tarefas" className="rtab-btn">
                    <GoTasklist className="inline-flex" fontSize={20} />{" "}
                    <span>
                      Tarefas{" "}
                      <span className="text-xs">({tasks?.length || 0})</span>
                    </span>
                  </label>
                  <label htmlFor="rtab-coment" className="rtab-btn">
                    <LiaCommentDotsSolid
                      className="inline-flex"
                      fontSize={20}
                    />{" "}
                    <span>
                      Observações{" "}
                      <span className="text-xs">
                        ({commentOnlyHistory?.length || 0})
                      </span>
                    </span>
                  </label>
                </div>

                {/* PANELS */}
                <div className="rtabs-panels mt-2 rounded-lg">
                  {/* Painel TAREFAS */}
                  <div className="rtab-panel">
                    {!isEditingTask && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => {
                            setIsEditingTask(true);
                          }}
                        >
                          Adicionar tarefa
                        </Button>
                      </div>
                    )}

                    {isEditingTask && (
                      <div>
                        <div className="flex flex-wrap gap-1.5">
                          {ACTION_PRESETS.map(({ key, label, icon: Icon }) => {
                            const active = actionType === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => {
                                  setActionType(key);
                                  if (!actionNoteHtml?.trim())
                                    setActionNoteHtml(defaultNoteFor(key));
                                }}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs transition-all ${
                                  active
                                    ? "bg-gray-900 border-gray-900 text-white"
                                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <Icon size={14} />
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3">
                          <label className="text-sm text-gray-700 font-medium mb-1 block">
                            Descrição
                          </label>
                          <TiptapEditor
                            value={actionNoteHtml}
                            onChange={setActionNoteHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Detalhe a ação (opcional)"
                          />
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm text-gray-700 font-medium mb-1 block">
                              Data do prazo
                            </label>
                            <TextInput
                              type="date"
                              value={actionDueDate}
                              onChange={(e) => setActionDueDate(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-700 font-medium mb-1 block">
                              Horário do prazo
                            </label>
                            <TaskTimePicker
                              value={actionDueTime}
                              onChange={setActionDueTime}
                              step={15}
                              min="08:00"
                              max="20:00"
                              preferAbove
                              leftIcon={
                                <span className="text-gray-400">🕒</span>
                              }
                            />
                          </div>

                          {hasGoogleCalendar && actionDueDate && (
                            <div className="mt-3 rounded-2xl border border-dashed border-gray-300 bg-slate-50 p-3 text-xs text-gray-700 space-y-2">
                              <div className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                                Disponibilidade no Google Calendar
                              </div>
                              {availabilityLoading ? (
                                <div className="text-gray-500">Buscando...</div>
                              ) : availabilityError ? (
                                <div className="text-rose-600">
                                  {availabilityError}
                                </div>
                              ) : calendarAvailability.length ? (
                                <div className="space-y-1">
                                  {calendarAvailability.map((event) => (
                                    <div
                                      key={event.id}
                                      className="flex items-center justify-between text-[13px] text-gray-700"
                                    >
                                      <span className="font-semibold text-gray-800">
                                        {formatEventTimeRange(event)}
                                      </span>
                                      <span className="ml-3 truncate text-gray-600">
                                        {event.summary || "Evento sem título"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-gray-500">
                                  Sem compromissos registrados para este dia.
                                </div>
                              )}
                            </div>
                          )}

                          {conflictingEvents.length > 0 && (
                            <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                              <div className="font-semibold text-rose-700">
                                Horário escolhido em conflito no Google Calendar
                              </div>
                              <ul className="list-disc ml-4 mt-1 space-y-1">
                                {conflictingEvents.map((event) => (
                                  <li key={event.id}>
                                    <span className="font-semibold text-rose-800">
                                      {formatEventTimeRange(event)}
                                    </span>{" "}
                                    — {event.summary || "Evento sem título"}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {actionType === "whatsapp" && (
                            <div className="sm:col-span-2">
                              <label className="text-sm text-gray-700 font-medium mb-1 block">
                                Telefone (opcional p/ abrir WhatsApp)
                              </label>
                              <TextInput
                                type="tel"
                                placeholder="DDD + número"
                                value={actionPhone}
                                onChange={(e) => setActionPhone(e.target.value)}
                              />
                            </div>
                          )}
                        </div>

                        <div className="mt-5">
                          <AutoCompleteInput
                            label="Responsáveis da tarefa"
                            name="task-responsible"
                            options={userOptions}
                            value={actionResponsibleIds || []}
                            onChange={(opt) =>
                              setActionResponsibleIds(
                                Array.isArray(opt)
                                  ? opt.map((o) => o.value)
                                  : opt?.value
                                  ? [opt.value]
                                  : []
                              )
                            }
                            isLoading={loadingUsers}
                            isMulti
                            isClearable
                          />
                        </div>

                        <div className="mt-4 flex flex-wrap gap-3">
                          <Button
                            size="sm"
                            onClick={handleSaveNextAction}
                            disabled={isUploadingTask}
                            aria-busy={isUploadingTask}
                          >
                            {isUploadingTask
                              ? uploadPct
                                ? `Enviando… ${uploadPct}%`
                                : "Enviando…"
                              : "Salvar tarefa"}
                          </Button>

                          {actionType === "whatsapp" && actionPhone && (
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => {
                                const msg = encodeURIComponent(
                                  (actionNoteHtml || "")
                                    .replace(/<[^>]+>/g, " ")
                                    .replace(/\s+/g, " ")
                                    .trim()
                                );
                                const phone = actionPhone.replace(/\D/g, "");
                                window.open(
                                  `https://wa.me/${phone}?text=${msg}`,
                                  "_blank"
                                );
                              }}
                            >
                              Abrir WhatsApp
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-4"></div>
                  <section className="rtab-panel">
                    {loadingTasks ? (
                      <div
                        className=" flex items-center justify-center bg-white flex-col gap-2"
                        style={{ minHeight: "360px" }}
                      >
                        <Spinner size={"xl"} />
                        <div>Carregando tarefas...</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500">
                                Tarefas em aberto
                              </p>
                            </div>
                            <span className="text-xs text-gray-400">
                              {openTasks.length}{" "}
                              {openTasks.length === 1 ? "tarefa" : "tarefas"}
                            </span>
                          </div>
                          {openTasks.length ? (
                            <div className="p-4">
                              <TaskListCompact
                                tasks={openTasks}
                                loadingTasks={loadingTasks}
                                editingTaskId={editingTaskId}
                                editType={editType}
                                editNoteHtml={editNoteHtml}
                                editDueDate={editDueDate}
                                editDueTime={editDueTime}
                                editResponsibleIds={editResponsibleIds}
                                setEditType={setEditType}
                                setEditNoteHtml={setEditNoteHtml}
                                setEditDueDate={setEditDueDate}
                                setEditDueTime={setEditDueTime}
                                setEditResponsibleIds={setEditResponsibleIds}
                                labelMap={labelMap}
                                getUserNameById={getUserNameById}
                                userOptions={userOptions}
                                loadingUsers={loadingUsers}
                                startEditTask={startEditTask}
                                saveEditTask={saveEditTask}
                                cancelEditTask={cancelEditTask}
                                handleToggleTaskStatus={handleToggleTaskStatus}
                                handleDeleteTask={handleDeleteTask}
                                onDeleteAttachment={handleDeleteAttachment}
                                onConfirmComplete={openConfirmComplete}
                              />
                            </div>
                          ) : (
                            <div className="px-4 py-6 text-sm text-gray-500">
                              Nenhuma tarefa em aberto.
                            </div>
                          )}
                        </section>

                        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500">
                                Tarefas concluídas
                              </p>
                            </div>
                            <span className="text-xs text-gray-400">
                              {completedTasks.length}{" "}
                              {completedTasks.length === 1
                                ? "tarefa"
                                : "tarefas"}
                            </span>
                          </div>
                          {completedTasks.length ? (
                            <div className="p-4">
                              <TaskListCompact
                                tasks={completedTasks}
                                loadingTasks={loadingTasks}
                                editingTaskId={editingTaskId}
                                editType={editType}
                                editNoteHtml={editNoteHtml}
                                editDueDate={editDueDate}
                                editDueTime={editDueTime}
                                editResponsibleIds={editResponsibleIds}
                                setEditType={setEditType}
                                setEditNoteHtml={setEditNoteHtml}
                                setEditDueDate={setEditDueDate}
                                setEditDueTime={setEditDueTime}
                                setEditResponsibleIds={setEditResponsibleIds}
                                labelMap={labelMap}
                                getUserNameById={getUserNameById}
                                userOptions={userOptions}
                                loadingUsers={loadingUsers}
                                startEditTask={startEditTask}
                                saveEditTask={saveEditTask}
                                cancelEditTask={cancelEditTask}
                                handleToggleTaskStatus={handleToggleTaskStatus}
                                handleDeleteTask={handleDeleteTask}
                                onDeleteAttachment={handleDeleteAttachment}
                                onConfirmComplete={openConfirmComplete}
                              />
                            </div>
                          ) : (
                            <div className="px-4 py-6 text-sm text-gray-500">
                              Nenhuma tarefa concluída ainda.
                            </div>
                          )}
                        </section>
                      </div>
                    )}
                  </section>

                  {/* Painel COMENTÁRIOS */}
                  <section className="rtab-panel">
                    <div className="rounded-2xl overflow-hidden -mt-3">
                      <div className="p-2">
                        <div className=" overflow-hidden">
                          <TiptapEditor
                            value={commentHtml}
                            onChange={setCommentHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Escreva seu comentário…"
                            className="comment-editor"
                            minHeight={50}
                          />
                          <div className="px-1.5 py-2.5 bg-white/70 flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              color="light"
                              onClick={() =>
                                window.confirm("Limpar texto?") &&
                                setCommentHtml("")
                              }
                            >
                              Limpar
                            </Button>
                            <Button size="sm" onClick={handleAddComment}>
                              Enviar
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="p-1 pt-3 pr-2 rounded-md bg-gray-50">
                        {commentOnlyHistory.length > 0 ? (
                          <div className="max-h-[340px] overflow-y-auto soft-scroll px-2">
                            <ul className="space-y-2">
                              {commentOnlyHistory.map((h) => {
                                const name =
                                  h.User?.name ||
                                  getUserNameById?.(h.userId || h.user_id) ||
                                  "Não atribuído";
                                const initials = name
                                  .split(" ")
                                  .map((s) => s[0])
                                  .slice(0, 2)
                                  .join("")
                                  .toUpperCase();
                                const ts = new Date(h.createdAt).toLocaleString(
                                  "pt-BR"
                                );
                                const edited = !!(h.editedAt || h.isEdited);
                                return (
                                  <li
                                    key={h.id}
                                    className="group border-b border-gray-200 bg-white p-2 transition-shadow  relative"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 grid place-items-center text-[11px] font-semibold shrink-0">
                                        {initials}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-900 truncate">
                                            {name}
                                          </span>
                                          <span className="hidden sm:inline text-[11px] text-gray-400">
                                            •
                                          </span>
                                          <span className="hidden sm:inline text-[11px] text-gray-500">
                                            {ts}{" "}
                                            {edited && (
                                              <span className="ml-1 text-gray-400">
                                                (editado)
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                        <div className="sm:hidden text-[11px] text-gray-500">
                                          {ts}{" "}
                                          {edited && (
                                            <span className="ml-1 text-gray-400">
                                              (editado)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Conteúdo ou editor inline */}
                                    {editingCommentId === h.id ? (
                                      <TiptapEditor
                                        value={editCommentHtml}
                                        onChange={setEditCommentHtml}
                                        users={(availableUsers || []).map(
                                          (u) => ({
                                            id: u.id,
                                            value:
                                              u.name || u.email || "Usuário",
                                            email: u.email,
                                            avatar: u.avatarUrl,
                                          })
                                        )}
                                        placeholder="Edite seu comentário…"
                                      />
                                    ) : (
                                      <div
                                        className="prose prose-sm max-w-none mt-2 text-gray-800 leading-relaxed text-sm"
                                        dangerouslySetInnerHTML={{
                                          __html: h.text || "",
                                        }}
                                      />
                                    )}

                                    {isCommentOwner(h) && (
                                      <div className="absolute top-4 right-4 flex justify-end gap-3">
                                        {editingCommentId !== h.id ? (
                                          <>
                                            <button
                                              className="text-[16px] text-blue-600 underline"
                                              onClick={() => {
                                                setEditingCommentId(h.id);
                                                setEditCommentHtml(
                                                  h.text || ""
                                                );
                                              }}
                                            >
                                              <MdOutlineModeEdit />
                                            </button>
                                            <button
                                              className="text-[12px] text-red-600 underline"
                                              onClick={() =>
                                                handleDeleteComment(h)
                                              }
                                            >
                                              <FaRegTrashCan />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              className="text-[12px] text-gray-700 underline"
                                              onClick={() => {
                                                setEditingCommentId(null);
                                                setEditCommentHtml("");
                                              }}
                                            >
                                              Cancelar
                                            </button>
                                            <button
                                              className="text-[12px] text-blue-600 underline"
                                              onClick={() =>
                                                handleSaveEditedComment(h)
                                              }
                                            >
                                              Salvar
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : (
                          <div className="text-center py-10 text-sm text-gray-600 flex flex-col items-center justify-center">
                            <span className="text-3xl">💬</span>
                            <span>Ainda não há observações.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                  {/* Painel Atividades */}
                  <section className="rtab-panel relative">
                    {loadingActivities ? (
                      <div
                        className=" flex items-center justify-center bg-white flex-col gap-2"
                        style={{ minHeight: "360px" }}
                      >
                        <Spinner size={"xl"} />
                        <div>Carregando atividades...</div>
                      </div>
                    ) : (
                      <div className="rounded-2xl overflow-hidden">
                        <div className="rounded-md space-y-4">
                          {upcomingTasks.length > 0 && (
                            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                              <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-100">
                                Eventos futuros
                              </div>
                              <ul className="divide-y divide-gray-100">
                                {upcomingTasks.map((futureTask) => {
                                  const responsibleLabel =
                                    formatTaskResponsibleLabel(futureTask);
                                  return (
                                    <li
                                      key={futureTask.id}
                                      className="px-4 py-3 text-sm text-gray-900 space-y-1"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-semibold text-gray-800">
                                          {labelMap[futureTask.type] ||
                                            "Tarefa"}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {moment(futureTask.dueDate).format(
                                            "DD/MM/YYYY [às] HH:mm"
                                          )}
                                        </span>
                                      </div>
                                      {responsibleLabel && (
                                        <div className="text-[11px] text-gray-600">
                                          Responsável:{" "}
                                          <span className="font-medium text-gray-800">
                                            {responsibleLabel}
                                          </span>
                                        </div>
                                      )}
                                      {futureTask.note && (
                                        <div
                                          className="text-[11px] text-gray-600 leading-relaxed"
                                          dangerouslySetInnerHTML={{
                                            __html: futureTask.note,
                                          }}
                                        />
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {activities?.length > 0 ? (
                            <div className="max-h-[65vh] overflow-y-auto soft-scroll">
                              <div className="space-y-4">
                                {Object.keys(activitiesByMonth).map((month) => (
                                  <section
                                    key={month}
                                    className="border border-gray-100 rounded-xl bg-white/60"
                                  >
                                    {/* Cabeçalho do mês */}
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                                      <div className="text-xs font-semibold text-gray-700 tracking-wide uppercase">
                                        {month.charAt(0).toUpperCase() +
                                          month.slice(1)}
                                      </div>
                                      <span className="text-[11px] text-gray-500">
                                        {activitiesByMonth[month].length}{" "}
                                        atividade
                                        {activitiesByMonth[month].length > 1 &&
                                          "s"}
                                      </span>
                                    </div>

                                    {/* Lista das atividades do mês */}
                                    <ul className="divide-y divide-gray-100">
                                      {activitiesByMonth[month].map(
                                        (h, index) => {
                                          const isTask =
                                            h.targetType === "TASK";
                                          const hasDueDate =
                                            h.after &&
                                            "dueDate" in (h.after || {});
                                          const hasResponsible =
                                            h.after && h.after.responsibleId;

                                          return (
                                            <li
                                              key={index}
                                              className="group px-3 py-2.5 hover:bg-gray-50/80 transition-colors text-xs"
                                            >
                                              <div className="flex items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                  {/* Linha de título: tipo de ação + card */}
                                                  <div className="flex flex-wrap items-center gap-2">
                                                    <span
                                                      className={
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium " +
                                                        getActionColor(h.action)
                                                      }
                                                    >
                                                      {getActionLabel(h.action)}
                                                    </span>
                                                  </div>

                                                  {/* Descrição principal */}
                                                  <p className="mt-1 text-[11px] text-gray-700">
                                                    {h?.description}
                                                  </p>

                                                  {/* Rodapé: data + usuário */}
                                                  <div className="mt-1 text-[11px] text-gray-400">
                                                    {moment(
                                                      h?.createdAt
                                                    ).format(
                                                      "DD/MM/YYYY [às] HH:mm"
                                                    )}{" "}
                                                    ·{" "}
                                                    <span>
                                                      por{" "}
                                                      <span className="font-medium text-gray-600">
                                                        {h?.actor?.name ||
                                                          h?.actor?.email}
                                                      </span>
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </li>
                                          );
                                        }
                                      )}
                                    </ul>
                                  </section>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-10 text-sm text-gray-600 flex flex-col items-center justify-center">
                              <span className="text-3xl">💬</span>
                              <span>Ainda não há atividades do card.</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </section>
                </div>

                <div style={{ height: "80px" }}></div>
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* Modal de edição de tarefa existente */}
        <TaskEditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
          }}
          taskBeingEdited={taskBeingEdited}
          editingData={{
            editType,
            editNoteHtml,
            editDueDate,
            editDueTime,
            editResponsibleIds,
          }}
          setEditType={setEditType}
          setEditNoteHtml={setEditNoteHtml}
          setEditDueDate={setEditDueDate}
          setEditDueTime={setEditDueTime}
          setEditResponsibleIds={setEditResponsibleIds}
          userOptions={userOptions}
          loadingUsers={loadingUsers}
          saveEditTask={saveEditTask}
          cancelEditTask={cancelEditTask}
          onDeleteAttachment={handleDeleteAttachment}
          editFiles={editFiles}
          setEditFiles={setEditFiles}
          labelMap={labelMap}
          quillFormats={[]}
          quillModules={{}}
          prettySize={prettySize}
          fileIconFor={fileIconFor}
        />

        <Modal show={!!confirmCompleteTask} onClose={closeConfirmComplete}>
          <Modal.Header>Concluir tarefa</Modal.Header>
          <Modal.Body>
            <div className="text-sm text-gray-700">
              Tem certeza de que deseja marcar esta tarefa como <b>concluída</b>
              ?
            </div>
            {confirmCompleteTask?.note && (
              <div className="mt-2 p-2 rounded bg-gray-50 border text-xs text-gray-600">
                <div className="font-medium mb-1">Resumo:</div>
                <div
                  className="prose prose-sm text-gray-700 max-w-none"
                  dangerouslySetInnerHTML={{ __html: confirmCompleteTask.note }}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" onClick={confirmCompleteNow}>
              Concluir
            </Button>
            <Button size="sm" color="light" onClick={closeConfirmComplete}>
              Cancelar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showLeadEditModal}
          onClose={() => setShowLeadEditModal(false)}
          size="4xl"
        >
          <Modal.Header>Editar cliente</Modal.Header>
          <Modal.Body>
            {leadFormLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner size="sm" />
                <span className="ml-2 text-sm text-gray-500">
                  Carregando cliente...
                </span>
              </div>
            ) : leadFormInitialData ? (
              <LeadCreateForm
                initialData={leadFormInitialData}
                externalSubmit={handleLeadUpdate}
                onSuccess={handleLeadEditSuccess}
                submitLabel="Salvar alterações"
              />
            ) : (
              <div className="text-sm text-gray-500">Cliente indisponível.</div>
            )}
          </Modal.Body>
        </Modal>
      </div>

      {/* util styles */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar{ display:none; }
        .no-scrollbar{ -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>
      <style>{`
  .rich-text p{ margin:.35rem 0; }
  .rich-text ul,.rich-text ol{ margin:.35rem 0 .35rem 0; padding-left:1.25rem; }
  .rich-text ul{ list-style: disc outside; }
  .rich-text ol{ list-style: decimal outside; }
  .rich-text li{ margin:.2rem 0; }
  .rich-text h1{ font-size:1.25rem; font-weight:700; margin:.4rem 0 .5rem; }
  .rich-text h2{ font-size:1.1rem; font-weight:700; margin:.4rem 0 .5rem; }
  .rich-text h3{ font-size:1rem; font-weight:700; margin:.35rem 0 .45rem; }
  .rich-text blockquote{ border-left:3px solid #e5e7eb; margin:.6rem 0; padding:.35rem .8rem; color:#4b5563; background:#f9fafb; border-radius:6px; }
  .rich-text a{ text-decoration: underline; color:#1d4ed8; }
  .rich-text code{ background:#f3f4f6; border:1px solid #e5e7eb; border-radius:6px; padding:1px 6px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace; font-size:.92em; }
  .rich-text pre{ background:#0b1020; color:#f3f4f6; border-radius:10px; padding:10px 12px; overflow:auto; border:1px solid #111827; }
  .rich-text pre code{ background:transparent; border:0; padding:0; color:inherit; font-size:.95em; }

  /* ====== Custom Right Tabs ====== */
  .rtab-input{ display:none; }
  .rtabs-nav .rtab-btn{
    position: relative;
    display:inline-flex; align-items:center; gap:.5rem;
    padding:.5rem .75rem;
    font-size:.875rem; font-weight:600; color:#4b5563; cursor:pointer;
    transition: background .2s, color .2s, border-color .2s, box-shadow .2s;
    white-space: nowrap;
  }
  .rtabs-nav .rtab-btn:hover{ background:#fff; border-color:#e5e7eb; color:#111827; }
  .rtab-dot{
    width:.5rem; height:.5rem; border-radius:9999px; background:#141414; display:inline-block;
  }
  .rtab-badge{
    display:inline-flex; align-items:center; justify-content:center;
    padding:0 .4rem; height:1.1rem; border-radius:.6rem;
    font-size:.70rem; font-weight:700; background:#eef2ff; color:#4f46e5; border:1px solid #e0e7ff;
  }
  /* States */
  #rtab-tarefas:checked ~ .rtabs-nav label[for="rtab-tarefas"],
  #rtab-add:checked ~ .rtabs-nav label[for="rtab-add"],
  #rtab-coment:checked ~ .rtabs-nav label[for="rtab-coment"],
  #rtab-logs:checked ~ .rtabs-nav label[for="rtab-logs"],
  #rtab-form:checked ~ .rtabs-nav label[for="rtab-form"]{
    border-bottom: solid 3px #e35412;
    color: #e35412;
  }
  #rtab-tarefas:checked ~ .rtabs-nav label[for="rtab-tarefas"] .rtab-dot,
  #rtab-add:checked ~ .rtabs-nav label[for="rtab-add"] .rtab-dot,
  #rtab-coment:checked ~ .rtabs-nav label[for="rtab-coment"] .rtab-dot,
  #rtab-form:checked ~ .rtabs-nav label[for="rtab-form"] .rtab-dot{ background:#fff; }

  .rtabs-panels .rtab-panel{ display:none; }
  #rtab-tarefas:checked ~ .rtabs-panels .rtab-panel:nth-of-type(1){ display:block; }
  #rtab-coment:checked  ~ .rtabs-panels .rtab-panel:nth-of-type(2){ display:block; }
  #rtab-logs:checked    ~ .rtabs-panels .rtab-panel:nth-of-type(3){ display:block; }
`}</style>
    </Modal>
  );
}
